import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import * as api from '../api.ts';
import { FullBleedList } from '../directory.tsx';
import { BuildingIcon } from '../icons.tsx';
import { PROCEDURES, defaultInstruction, type Procedure } from '../lib/procedures.ts';
import { Map, MapKey, type MapPoint } from '../map.tsx';
import { AddressAutocomplete } from '../lib/address-autocomplete.tsx';
import { ActivationRuleSetting } from '../lib/activation-rule.tsx';
import { nearMe } from '../lib/my-location.ts';
import { addressSearchRegion } from '../lib/search-region.ts';
import { reverseAddress } from '../lib/reverse-address.ts';
import { cornerIndex, drawingShape } from '../lib/draft-shape.ts';
import { OutlineControls } from '../lib/outline-controls.tsx';
import {
  DuplicateSiteFinding,
  duplicateFromError,
  useSiteMatch,
  type DuplicateSite,
} from '../site-duplicate.tsx';
import {
  Button,
  Cell,
  Empty,
  Field,
  Finding,
  InlineAction,
  Label,
  ListRow,
  Metric,
  Metrics,
  PageTitle,
  Row,
  Section,
  Select,
  Spinner,
  Status,
  Table,
  sentence,
} from '../ui.tsx';
import { classifyGap } from '../lib/readiness-gaps.ts';
import { QrCodes } from '../qr-codes.tsx';
import { useDisclosure } from '../lib/disclosure.ts';
import type { Screen } from '../shell.tsx';

/**
 * The screens an organisation lives in when nothing is happening — which is
 * 99% of the time (`dashboard-requirements.md` §4). Readiness is the product;
 * the activation console is the thing it exists to make unnecessary to think
 * about.
 */

export function SitePicker({
  sites,
  siteId,
  onChange,
}: {
  sites: api.Site[];
  siteId: string;
  onChange: (id: string) => void;
}) {
  if (sites.length <= 1) return null;
  return (
    <div className="w-[280px]">
      <Select
        label="Site"
        value={siteId}
        onChange={onChange}
        options={sites.map((s) => ({ value: s.id, label: s.name }))}
      />
    </div>
  );
}

/* ---------------------------------------------------------------- overview */

/**
 * The resting state of the whole organisation, in one sentence.
 *
 * Apple's charting guidance is explicit about this and it is the single
 * most useful thing found in the research pass: *display brief descriptive
 * text that serves as a headline or summary, helping people grasp essential
 * information at a glance* — the pattern Weather uses when it says "chance
 * of light rain in the next hour" above the hourly list. The numbers are
 * still there; they stop being the thing you have to interpret.
 *
 * It suits this product better than it suits Weather. A dashboard for a
 * system that is idle 99% of the time should be able to say so in words,
 * and "everything is ready" is the answer an admin actually opens this
 * screen to get.
 *
 * Counted from the same readiness responses the cards below render, so the
 * sentence can never disagree with what is under it — the failure that
 * would make it worse than no sentence at all.
 */
function OverviewSummary({ orgId, sites }: { orgId: string; sites: api.Site[] }) {
  const results = useQueries({
    queries: sites.map((site) => ({
      queryKey: ['readiness', orgId, site.id],
      queryFn: () => api.getReadiness(orgId, site.id),
    })),
  });

  if (results.some((r) => r.isLoading)) return null;

  const loaded = results.map((r) => r.data).filter(Boolean) as api.Readiness[];
  if (loaded.length === 0) return null;

  const notReady = loaded.filter((r) => !r.ready);
  const gaps = notReady.reduce((n, r) => n + r.gaps.length, 0);
  const buildings = loaded.length === 1 ? 'This building' : `All ${loaded.length} buildings`;

  const sentence =
    notReady.length === 0
      ? `${buildings} ${loaded.length === 1 ? 'is' : 'are'} ready.`
      : notReady.length === loaded.length
        ? `${loaded.length === 1 ? 'This building has' : `All ${loaded.length} buildings have`} something outstanding — ${gaps} ${gaps === 1 ? 'thing' : 'things'} in total.`
        : `${notReady.length} of ${loaded.length} buildings ${notReady.length === 1 ? 'has' : 'have'} something outstanding — ${gaps} ${gaps === 1 ? 'thing' : 'things'} in total.`;

  return (
    <p
      className={`mb-6 text-[17px] leading-[22px] ${
        notReady.length === 0 ? 'text-[var(--safe-text)]' : 'text-[var(--ink)]'
      }`}
    >
      {sentence}
    </p>
  );
}

/**
 * One question: **is every building ready?**
 *
 * Gaps are named in words rather than scored. A readiness percentage is a
 * number somebody optimises; "no Chief Warden is appointed" is a thing somebody
 * fixes. The API returns the sentences so that this screen, the mobile app and
 * the report cannot each invent their own wording.
 */
export function Overview({
  orgId,
  sites,
  onNavigate,
}: {
  orgId: string;
  sites: api.Site[];
  /**
   * Send the admin to the screen that fixes a gap, with the building it belongs
   * to selected first — the same contract Home's to-do rows used before this
   * became the home of the to-do list. `''` for a whole-org destination.
   */
  onNavigate: (screen: Screen, siteId: string) => void;
}) {
  return (
    <>
      <PageTitle sub="Every building, and what is missing from it.">Readiness</PageTitle>
      {sites.length === 0 ? (
        <Empty>No sites yet. Add the first building to start running drills against it.</Empty>
      ) : (
        <>
          <OverviewSummary orgId={orgId} sites={sites} />
          {sites.map((site) => (
            <SiteReadiness key={site.id} orgId={orgId} site={site} onNavigate={onNavigate} />
          ))}
        </>
      )}
    </>
  );
}

function SiteReadiness({
  orgId,
  site,
  onNavigate,
}: {
  orgId: string;
  site: api.Site;
  onNavigate: (screen: Screen, siteId: string) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['readiness', orgId, site.id],
    queryFn: () => api.getReadiness(orgId, site.id),
  });

  return (
    <Section title={site.name}>
      {isLoading || !data ? (
        <Spinner />
      ) : (
        <>
          <div className="mb-4">
            <Status tone={data.ready ? 'safe' : 'attention'}>
              {data.ready ? 'Ready' : `${data.gaps.length} gap${data.gaps.length === 1 ? '' : 's'}`}
            </Status>
          </div>

          {/* Each gap is a one-tap row to the screen that fixes it, on this
              building — the actionable to-do list that used to live on Home
              (`design-dashboard.md` §8). The wording stays the API's own
              sentence so this screen, the app and the report never disagree;
              `classifyGap` only decides where the tap lands. */}
          {data.gaps.length > 0 ? (
            <div className="mb-1">
              {data.gaps.map((gap) => {
                const { screen } = classifyGap(gap);
                return (
                  <ListRow
                    key={gap}
                    label={`Fix: ${sentence(gap)} at ${site.name}`}
                    onClick={() => onNavigate(screen, site.id)}
                  >
                    {sentence(gap)}
                  </ListRow>
                );
              })}
            </div>
          ) : null}

          <div className="mt-5">
            <Metrics>
              {/* A real count, not a percentage of a guess: how many people an
                  alert would address on this building's basis. Coverage-against-
                  stated-occupancy was retired (owner 2026-09-14) because the
                  denominator was an admin's guess; the growth KPI is measured
                  from real enrolment data, not this field. */}
              <Metric label="Alert audience" value={data.reach.audience} />
              <Metric label="Areas" value={data.zones} />
              <Metric label="Assembly points" value={data.assemblyPoints} />
              <Metric label="ECO roles filled" value={data.ecoAssignments} />
              <Metric
                label="Last drill"
                value={
                  data.lastDrillClosedAt
                    ? new Date(data.lastDrillClosedAt).toLocaleDateString()
                    : '—'
                }
              />
            </Metrics>
          </div>
        </>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------- sites */

/**
 * Straight-line distance, rounded to the nearest ten metres.
 *
 * Deliberately not a walking route. A route needs a routing service and would
 * be wrong the moment a street is closed by the thing being evacuated from,
 * whereas "how far away is it" is a fact about the site that stays true. AS
 * 3745 wants an assembly area far enough from the building to be safe from
 * falling debris and close enough that people actually go there, and that is a
 * distance question.
 */
function metresBetween(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return Math.round((2 * R * Math.asin(Math.sqrt(h))) / 10) * 10;
}

const ASSEMBLY_KIND_LABEL: Record<api.AssemblyKind, string> = {
  outdoor_assembly: 'Evacuation',
  indoor_shelter: 'Lockdown / shelter',
  reunification: 'Reunification',
};

const PROFILE_LABEL: Record<api.AccessibilityProfile, string> = {
  default: 'General',
  step_free: 'Step-free',
  low_vision: 'Low vision',
  low_hearing: 'Low hearing',
  cognitive: 'Cognitive',
};

/**
 * Which profiles have a written instruction, and which do not.
 *
 * Shown as present-or-absent per profile rather than as a count, because "3
 * instructions" tells an admin nothing about whether the one a wheelchair user
 * needs is among them — and that is the only question this cell exists to
 * answer.
 */
function ProfileCoverage({ instructions }: { instructions: api.AssemblyInstruction[] }) {
  const have = new Set(instructions.map((i) => i.profile));
  const profiles: api.AccessibilityProfile[] = ['default', 'step_free'];

  return (
    <span className="flex flex-wrap gap-1.5">
      {profiles.map((p) => (
        <span
          key={p}
          className={`rounded-[3px] border px-1.5 py-0.5 text-[11px] ${
            have.has(p)
              ? 'border-[var(--line)] text-[var(--ink-2)]'
              : 'border-[var(--attention)] text-[var(--attention)]'
          }`}
        >
          {PROFILE_LABEL[p]}
          {have.has(p) ? '' : ' missing'}
        </span>
      ))}
      {/* Languages beyond the first are worth a count, since an alert in a
          language somebody cannot read did not reach them (migration 0016). */}
      {instructions.length > 0 ? (
        <span className="mono self-center text-[11px] text-[var(--ink-2)]">
          {new Set(instructions.map((i) => i.language)).size} lang
        </span>
      ) : null}
    </span>
  );
}

/**
 * Recording what somebody found out about a point after it was created.
 *
 * Separate from the add form on purpose: a capacity somebody counted and an
 * accessibility survey that happened in March arrive weeks after the pin does,
 * and requiring them at creation means points go unrecorded while an admin goes
 * looking for a number.
 */
function PointEditor({
  orgId,
  siteId,
  points,
}: {
  orgId: string;
  siteId: string;
  points: api.AssemblyPoint[];
}) {
  const qc = useQueryClient();
  const [pointId, setPointId] = useState(points[0]?.id ?? '');
  const [capacity, setCapacity] = useState('');
  const [instruction, setInstruction] = useState('');
  const [profile, setProfile] = useState<api.AccessibilityProfile>('step_free');

  const selected = points.find((p) => p.id === pointId) ?? points[0];
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['points', orgId, siteId] });
    void qc.invalidateQueries({ queryKey: ['readiness', orgId, siteId] });
  };

  const update = useMutation({
    mutationFn: (body: Parameters<typeof api.updateAssemblyPoint>[3]) =>
      api.updateAssemblyPoint(orgId, siteId, pointId, body),
    onSuccess: refresh,
  });

  const write = useMutation({
    mutationFn: () =>
      api.setAssemblyInstruction(orgId, siteId, pointId, {
        profile,
        // One language for now: the org's own. Translation runs through the
        // machinery that already exists, on a calm afternoon, per routing.md.
        language: 'en',
        instruction,
      }),
    onSuccess: () => {
      setInstruction('');
      refresh();
    },
  });

  if (!selected) return null;

  return (
    <div className="border-t border-[var(--line)] pt-5">
      <div className="mb-4 w-[280px]">
        <Select
          label="Record something about"
          value={pointId}
          onChange={setPointId}
          options={points.map((p) => ({ value: p.id, label: p.name }))}
        />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-[160px]">
          <Field label="Capacity" value={capacity} onChange={setCapacity} placeholder="450" />
        </div>
        <Button
          disabled={!capacity || update.isPending}
          label={`Set capacity for ${selected.name}`}
          onClick={() => update.mutate({ capacity: Number(capacity) })}
        >
          Set capacity
        </Button>

        {/* Three buttons rather than a checkbox, because the third state is
            real: "nobody has checked" is not "no". */}
        <div className="ml-4 flex items-end gap-2">
          <div>
            <div className="mb-1">
              <Label>Step-free access</Label>
            </div>
            <div className="flex gap-2">
              <Button
                label={`${selected.name} is reachable without stairs`}
                onClick={() => update.mutate({ stepFreeAccess: true })}
              >
                Yes
              </Button>
              <Button
                label={`${selected.name} is not reachable without stairs`}
                onClick={() => update.mutate({ stepFreeAccess: false })}
              >
                No
              </Button>
            </div>
          </div>
        </div>

        {!selected.is_default ? (
          <Button
            label={`Make ${selected.name} the default`}
            onClick={() => update.mutate({ isDefault: true })}
          >
            Make default
          </Button>
        ) : null}
      </div>

      <div className="mt-6 max-w-[640px]">
        <div className="mb-3 w-[220px]">
          <Select
            label="Approach instruction for"
            value={profile}
            onChange={(v) => setProfile(v as api.AccessibilityProfile)}
            options={(
              ['default', 'step_free', 'low_vision', 'low_hearing', 'cognitive'] as const
            ).map((p) => ({ value: p, label: PROFILE_LABEL[p] }))}
          />
        </div>
        <Field
          label="How to get there, in words"
          value={instruction}
          onChange={setInstruction}
          placeholder="Out the north doors, along the ramp, turn left at the gate."
        />
        <p className="mt-2 text-[13px] text-[var(--ink-2)]">
          Written now rather than generated during an incident, so somebody who knows the building
          checks it first and it needs no signal on the day.
        </p>
        <div className="mt-3">
          <Button
            variant="primary"
            disabled={!instruction || write.isPending}
            onClick={() => write.mutate()}
          >
            Save instruction
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The PEEP register.
 *
 * The one screen in the product a warden cannot open. It holds health
 * information, so three things differ from every other table here:
 *
 * - **It is not on the nav.** It sits inside Sites, directly under assembly
 *   points, because the gap that matters is a join between the two — somebody
 *   needs a step-free route and nobody has written one — and putting them on
 *   separate screens would hide exactly that.
 * - **Lapsed plans stay visible**, greyed rather than dropped. The plan that
 *   expired last week is the one that needs a conversation, and a list that
 *   silently shortens is how somebody stops being catered for without anybody
 *   deciding it.
 * - **The wording is "needs" and never a diagnosis.** The column says what the
 *   arrangement is, because that is what a warden acts on and what the person
 *   agreed to.
 */
function PeepRegister({ orgId, siteId }: { orgId: string; siteId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState('');
  const [profile, setProfile] = useState<api.AccessibilityProfile>('step_free');
  const [need, setNeed] = useState('');
  const [assistedBy, setAssistedBy] = useState('');
  const [error, setError] = useState<string | null>(null);

  const peeps = useQuery({
    queryKey: ['peeps', orgId, siteId],
    queryFn: () => api.getPeeps(orgId, siteId),
    enabled: Boolean(siteId),
    // A staff account gets 404 by design; that is not an error worth retrying.
    retry: false,
  });

  const roster = useQuery({ queryKey: ['roster', orgId], queryFn: () => api.getRoster(orgId) });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['peeps', orgId, siteId] });
    void qc.invalidateQueries({ queryKey: ['readiness', orgId, siteId] });
  };

  const save = useMutation({
    mutationFn: () =>
      api.recordPeep(orgId, siteId, {
        userId,
        profile,
        assistanceRequired: need,
        assistedBy: assistedBy || null,
      }),
    onSuccess: () => {
      setNeed('');
      setUserId('');
      setAssistedBy('');
      setOpen(false);
      setError(null);
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const withdraw = useMutation({
    mutationFn: (peepId: string) => api.withdrawPeep(orgId, siteId, peepId),
    onSuccess: refresh,
  });

  // 404 is the documented answer for a non-admin, so the section explains
  // itself rather than rendering an error.
  if (peeps.error) {
    return (
      <Section title="Personal evacuation plans">
        <Empty>
          Only an administrator can open this register. It records health information, so it is not
          part of what a warden sees.
        </Empty>
      </Section>
    );
  }

  const people: api.Person[] = (roster.data?.members ?? []).filter((p) => !p.deprovisioned_at);

  return (
    <Section
      title="Personal evacuation plans"
      action={
        <Button label="Record a personal evacuation plan" onClick={() => setOpen((v) => !v)}>
          {open ? 'Cancel' : 'Record a plan'}
        </Button>
      }
    >
      {peeps.isLoading || !peeps.data ? (
        <Spinner />
      ) : (
        <>
          {peeps.data.readiness.no_step_free_route > 0 ? (
            <div className="mb-5">
              <Finding>
                <strong className="tabular">{peeps.data.readiness.no_step_free_route}</strong>{' '}
                {peeps.data.readiness.no_step_free_route === 1 ? 'person needs' : 'people need'} a
                step-free route to the assembly point, and none has been written. Add a step-free
                approach instruction above.
              </Finding>
            </div>
          ) : null}

          {peeps.data.peeps.length === 0 ? (
            <Empty>
              No plans are recorded. A plan is agreed with the person, never written about them.
            </Empty>
          ) : (
            <Table head={['Person', 'Needs', 'Assisted by', 'Until', '']}>
              {peeps.data.peeps.map((p) => (
                <Row key={p.id}>
                  <Cell wide>
                    <span className={p.live ? '' : 'text-[var(--ink-2)] line-through'}>
                      {p.person ?? 'Unnamed person'}
                    </span>
                    <span className="mono ml-2 text-[11px] text-[var(--ink-2)]">
                      {PROFILE_LABEL[p.profile].toUpperCase()}
                    </span>
                  </Cell>
                  <Cell>{p.assistance_required}</Cell>
                  <Cell>{p.assistant ?? <Status tone="attention">Nobody assigned</Status>}</Cell>
                  <Cell>
                    {p.expires_at ? (
                      <Status tone={p.live ? 'neutral' : 'attention'}>
                        {new Date(p.expires_at).toLocaleDateString()}
                      </Status>
                    ) : (
                      'Ongoing'
                    )}
                  </Cell>
                  <Cell>
                    <Button
                      variant="quiet"
                      label={`Withdraw the plan for ${p.person ?? 'this person'}`}
                      onClick={() => withdraw.mutate(p.id)}
                    >
                      Withdraw
                    </Button>
                  </Cell>
                </Row>
              ))}
            </Table>
          )}

          {open ? (
            <div className="mt-6 max-w-[640px] border-t border-[var(--line)] pt-5">
              <div className="flex flex-wrap gap-3">
                <div className="w-[240px]">
                  <Select
                    label="Person"
                    value={userId}
                    onChange={setUserId}
                    options={[
                      { value: '', label: 'Choose someone' },
                      ...people.map((p) => ({
                        value: p.user_id,
                        label: p.display_name ?? p.email ?? p.user_id,
                      })),
                    ]}
                  />
                </div>
                <div className="w-[200px]">
                  <Select
                    label="Profile"
                    value={profile}
                    onChange={(v) => setProfile(v as api.AccessibilityProfile)}
                    options={(
                      ['step_free', 'low_vision', 'low_hearing', 'cognitive', 'default'] as const
                    ).map((v) => ({ value: v, label: PROFILE_LABEL[v] }))}
                  />
                </div>
                <div className="w-[240px]">
                  <Select
                    label="Assisted by"
                    value={assistedBy}
                    onChange={setAssistedBy}
                    options={[
                      { value: '', label: 'Nobody yet' },
                      ...people
                        .filter((p) => p.user_id !== userId)
                        .map((p) => ({
                          value: p.user_id,
                          label: p.display_name ?? p.email ?? p.user_id,
                        })),
                    ]}
                  />
                </div>
              </div>

              <div className="mt-4">
                <Field
                  label="What they need"
                  value={need}
                  onChange={setNeed}
                  placeholder="Evacuation chair and two people. Cannot use the north stairs."
                />
              </div>

              <p className="mt-2 text-[13px] text-[var(--ink-2)]">
                Saving records that this was agreed with the person today. Amending it later records
                the agreement again, because a change to what somebody needs is a conversation
                rather than an edit.
              </p>

              {error ? (
                <div className="mt-3">
                  <Finding>{error}</Finding>
                </div>
              ) : null}

              <div className="mt-4">
                <Button
                  variant="primary"
                  disabled={!userId || !need || save.isPending}
                  onClick={() => save.mutate()}
                >
                  Save plan
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </Section>
  );
}

/**
 * Per-building procedure instructions (migration 0093).
 *
 * Each building stores its own instruction per procedure type. It is BOTH a
 * readiness reference AND the default wording the raise flow lands on for that
 * procedure at that building, replacing the built-in default. A building that
 * has written none falls back to the built-in default from the shared module
 * (`../lib/procedures.ts`), which is what the placeholder shows — so an admin
 * can see exactly what would be sent before deciding whether to override it.
 *
 * Communication has no built-in default (an evacuation has a nearest exit; a
 * "communication" has only whatever the operator types), so its placeholder
 * says so rather than showing empty words.
 */
function ProcedureEditor({ orgId, siteId }: { orgId: string; siteId: string }) {
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<Partial<Record<Procedure, string>>>({});
  const [savedFor, setSavedFor] = useState<Procedure | null>(null);
  // Which procedure's last save failed (M1). The draft is left dirty so the
  // words are not lost, and the error clears the moment the box changes or a
  // save is retried.
  const [saveErrorFor, setSaveErrorFor] = useState<Procedure | null>(null);

  const stored = useQuery({
    queryKey: ['siteProcedures', orgId, siteId],
    queryFn: () => api.siteProcedures(orgId, siteId),
    enabled: Boolean(siteId),
  });

  // Seed the editable drafts from the server whenever they arrive or change, so
  // the boxes show the stored words while staying editable. `null` (no stored
  // row) seeds to an empty string, which reads as the placeholder default.
  const storedProcedures = stored.data?.procedures;
  useEffect(() => {
    if (!storedProcedures) return;
    const next: Partial<Record<Procedure, string>> = {};
    for (const p of storedProcedures) next[p.procedure] = p.instruction ?? '';
    setDrafts(next);
  }, [storedProcedures]);

  const save = useMutation({
    mutationFn: (p: Procedure) => api.setSiteProcedure(orgId, siteId, p, drafts[p] ?? ''),
    onMutate: () => setSaveErrorFor(null),
    onSuccess: (_res, p) => {
      setSavedFor(p);
      void qc.invalidateQueries({ queryKey: ['siteProcedures', orgId, siteId] });
    },
    // A failed PUT (403 stale-freshness, network, 500) must not be silent — and
    // the draft stays dirty (we never reset it here) so the words are not lost.
    onError: (_e, p) => setSaveErrorFor(p),
  });

  return (
    <Section title="Procedures">
      <p className="mb-4 max-w-prose text-[14px] text-[var(--ink-2)]">
        What this building tells people to do, per procedure. These words become the default when
        you raise that procedure here — you can still change them in the moment, and this never
        overwrites what you type during an incident. Leave a box blank to use the standard wording
        shown as the placeholder.
      </p>

      {stored.isError ? (
        <div className="rounded-[var(--radius-1)] border border-[var(--line)] bg-[var(--paper-2)] p-4">
          <p className="mb-3 text-[14px] text-[var(--ink)]">
            The stored procedure instructions could not be loaded.
          </p>
          <Button
            variant="secondary"
            label="Try loading procedures again"
            onClick={() => stored.refetch()}
          >
            Try again
          </Button>
        </div>
      ) : stored.isLoading || !stored.data ? (
        <Spinner />
      ) : (
        <div className="space-y-5">
          {PROCEDURES.map((def) => {
            const value = drafts[def.value] ?? '';
            const server = stored.data?.procedures.find((p) => p.procedure === def.value);
            const serverValue = server?.instruction ?? '';
            const dirty = value !== serverValue;
            const builtIn = defaultInstruction(def.value);
            const placeholder =
              def.value === 'communication'
                ? 'No standard wording — type what to tell people.'
                : builtIn;
            // The exact wording that WILL be sent when the box is blank, as real
            // helper text at an AA-passing token (M3) — not the ~50% placeholder
            // colour, which fails contrast and reads as "nothing here".
            const blankHelp =
              def.value === 'communication'
                ? 'No standard wording — sent blank unless you type something.'
                : `Standard wording, sent if left blank: “${builtIn}”`;
            // The visible label flips to "Clear" when a blank draft will delete a
            // stored row; the accessible name must follow it (L1).
            const clearing = value.trim() === '' && serverValue !== '';
            return (
              <div key={def.value} className="max-w-[560px]">
                <label className="block">
                  <span className="mb-1.5 block">
                    <Label>{def.label}</Label>
                  </span>
                  <textarea
                    value={value}
                    placeholder={placeholder}
                    rows={2}
                    onChange={(e) => {
                      setSavedFor(null);
                      setSaveErrorFor(null);
                      setDrafts((d) => ({ ...d, [def.value]: e.target.value }));
                    }}
                    className="w-full rounded-[var(--radius-1)] border border-[var(--line-2)] bg-[var(--paper)] px-3 py-2 text-[14px] text-[var(--ink)] outline-none focus:border-[var(--focus)]"
                  />
                </label>
                <p className="mt-1 text-[13px] leading-[18px] text-[var(--ink-2)]">{blankHelp}</p>
                <div className="mt-2 flex items-center gap-3">
                  <Button
                    disabled={!dirty || save.isPending}
                    label={
                      clearing
                        ? `Clear the ${def.label.toLowerCase()} instruction`
                        : `Save the ${def.label.toLowerCase()} instruction`
                    }
                    onClick={() => save.mutate(def.value)}
                  >
                    {clearing ? 'Clear' : 'Save'}
                  </Button>
                  {savedFor === def.value && !dirty ? (
                    <Status tone="safe">Saved</Status>
                  ) : serverValue === '' ? (
                    <span className="text-[13px] text-[var(--ink-2)]">
                      Using the standard wording.
                    </span>
                  ) : null}
                </div>
                {saveErrorFor === def.value ? (
                  <div className="mt-2">
                    <Finding tone="alert">Couldn&rsquo;t save — not stored. Try again.</Finding>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

/**
 * The building at a glance, read before any edit control is touched — plus the
 * one action an operator opens this panel to take in an emergency: raise an
 * alert or run a drill against this building.
 *
 * Nothing here edits: the sections below own that. The numbers come from the
 * same readiness response the rest of the detail loads, so the summary can
 * never disagree with the sections under it (the OverviewSummary rule); the
 * address and phone come from the site row itself. The building page's own
 * header carries the building name and address as its title, so this restates
 * address and phone quietly rather than repeating the name as a second heading.
 *
 * It carries readiness status, a single presence glance, and the last drill —
 * and deliberately does NOT restate the alert audience or followers,
 * which are owned once by AlertAudience below (board finding, PR #448: the same
 * number under overlapping labels in two places let one page show two figures).
 */
function SiteSummaryHeader({
  orgId,
  siteId,
  site,
  onRaise,
}: {
  orgId: string;
  siteId: string;
  site: api.Site | undefined;
  /** Into the raise/drill flow for this building; omitted where there is none. */
  onRaise?: () => void;
}) {
  const { data } = useQuery({
    queryKey: ['readiness', orgId, siteId],
    queryFn: () => api.getReadiness(orgId, siteId),
    enabled: Boolean(siteId),
  });

  // "On site now" is only honest where the site counts presence; on a roster
  // site the same number is the roster, not who is actually in the building, so
  // it is labelled for what it is rather than mislabelled. This is the ONE
  // presence glance on the page (the site's own presence figure, peopleToPlace);
  // the whole alert-reach block — audience and followers — is owned
  // solely by AlertAudience below, so no number is shown or labelled twice.
  //
  // LOCKSTEP: `data.audienceBasis` read here and `data.reach.basis` read in
  // AlertAudience must always agree — both derive from `site_uses_presence()`
  // server-side. If they ever disagreed, the header's presence glance and the
  // audience section would describe two different populations of one building.
  const presence = data?.audienceBasis === 'presence';

  return (
    <div className="mb-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          {data ? (
            <Status tone={data.ready ? 'safe' : 'attention'}>
              {data.ready
                ? 'Ready'
                : `${data.gaps.length} ${data.gaps.length === 1 ? 'gap' : 'gaps'}`}
            </Status>
          ) : null}
          <dl className="mt-3 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-[14px]">
            <dt className="text-[var(--ink-2)]">Address</dt>
            <dd className="m-0 text-[var(--ink)]">
              {site?.address ?? <span className="text-[var(--ink-3)]">Not set</span>}
            </dd>
            {/* The sub-premise (migration 0110), shown only when set — a
                whole-building site has neither and reads cleaner without them. */}
            {site?.unit ? (
              <>
                <dt className="text-[var(--ink-2)]">Unit / Suite</dt>
                <dd className="m-0 text-[var(--ink)]">{site.unit}</dd>
              </>
            ) : null}
            {site?.level ? (
              <>
                <dt className="text-[var(--ink-2)]">Level</dt>
                <dd className="m-0 text-[var(--ink)]">{site.level}</dd>
              </>
            ) : null}
            <dt className="text-[var(--ink-2)]">Phone</dt>
            <dd className="m-0 text-[var(--ink)]">
              {site?.phone ?? <span className="text-[var(--ink-3)]">Not set</span>}
            </dd>
          </dl>
        </div>
        {onRaise ? (
          <Button
            variant="primary"
            label={`Raise an alert or run a drill at ${site?.name ?? 'this building'}`}
            onClick={onRaise}
          >
            Raise / run a drill
          </Button>
        ) : null}
      </div>

      <div className="mt-6">
        <Metrics>
          {/* The one presence glance: the site's own presence/roster figure,
              with a presence-specific label. Followers and the alert audience
              live once, in AlertAudience below — never restated here. */}
          <Metric
            label={presence ? 'On site now' : 'On the list'}
            value={data?.peopleToPlace ?? '—'}
          />
          <Metric
            label="Last drill"
            value={
              data?.lastDrillClosedAt ? new Date(data.lastDrillClosedAt).toLocaleDateString() : '—'
            }
          />
        </Metrics>
      </div>
    </div>
  );
}

/**
 * Who will be alerted for this building.
 *
 * The one question an admin has before an incident: if an alert went out now,
 * who hears it. Every number is the readiness reach the overview
 * already computes (the same `getReadiness` query, deduped by react-query), so
 * this screen, the report and the overview never disagree — a second definition
 * of "reach" here would be a number that reassures about the wrong people.
 *
 * **Followers are counted beside the audience, never inside it.** They cannot
 * confirm safe (their answer would corrupt the number that decides whether
 * anyone goes back inside — CLAUDE.md, the follower rule), so they sit outside
 * the accountability denominator here exactly as they do in the ring and the
 * report.
 *
 * GAP (G20 — do not invent numbers): `getReadiness` exposes `reach` as a
 * *single* audience figure keyed on the site's audience basis (`presence` → who
 * is checked in on site now; `roster` → the whole roster), with
 * reachable/unreachable underneath. It does **not** break the audience down per
 * alert scope — there is no "staff on site" figure separate from "everyone on
 * site". So this renders the whole-site audience and the followers it actually
 * has, and does not fabricate a staff-only reach. When a per-scope reach lands
 * (an `audience_scope` breakdown on the readiness response), add the "staff on
 * site" row here beside "everyone on site".
 */
function AlertAudience({
  orgId,
  siteId,
  site,
  onPeople,
}: {
  orgId: string;
  siteId: string;
  site: api.Site | undefined;
  /** Drill down to the named list — People filtered to this building. */
  onPeople?: (siteId: string) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['readiness', orgId, siteId],
    queryFn: () => api.getReadiness(orgId, siteId),
    enabled: Boolean(siteId),
  });

  // Presence and roster count different populations, so the audience prose is
  // worded for what it actually is rather than a bare "on site" that would
  // overclaim on a roster-basis building.
  //
  // LOCKSTEP: `data.reach.basis` read here and `data.audienceBasis` read in
  // SiteSummaryHeader must always agree — both derive from `site_uses_presence()`
  // server-side (see the header's note). This section owns the alert-reach block;
  // the header owns the single presence glance.
  const presence = data?.reach.basis === 'presence';
  const followers = site?.followers ?? 0;

  return (
    <Section title="Alert audience">
      {isLoading || !data ? (
        <Spinner />
      ) : (
        <>
          <p className="mb-4 max-w-prose text-[14px] text-[var(--ink-2)]">
            Everyone counted for this building
            {presence
              ? ' — those checked in on site'
              : ' — everyone on your list, because this building does not count presence'}
            . Exactly who is alerted depends on the scope you choose when you raise. Followers are
            told separately and are never part of the count that decides whether the building is
            clear.
          </p>
          <Metrics>
            {/* The alert audience an activation snapshots — everyone this
                building would target on the current basis. Labelled as the reach
                rather than "on site now": the header owns the single presence
                glance, and this figure is who an alert addresses, not a second
                presence count under the same words. There is no separate
                staff-only figure to show (see the GAP note above). */}
            <Metric label="Alert audience" value={data.reach.audience} />
            {/* Of that audience, who a real send could actually reach on some
                channel, and who it could not — the readiness twin of the live
                unreachable count. */}
            <Metric label="Reachable on a channel" value={data.reach.reachable} />
            <Metric label="Unreachable" value={data.reach.unreachable} />
            {/* Counted apart from the audience, on purpose. */}
            <Metric label="Followers" value={followers} />
          </Metrics>
        </>
      )}

      {/* The drill-down to the named list: People filtered to this building,
          reused rather than a duplicate roster table here. It is navigation, so
          it does not wait on the reach numbers. Omitted where the caller wires
          no navigation (e.g. the standalone/Overview use). */}
      {onPeople ? (
        <div className="mt-4">
          <ListRow
            label={`See the people who would be alerted at ${site?.name ?? 'this building'}`}
            onClick={() => onPeople(siteId)}
          >
            See who would be alerted
          </ListRow>
        </div>
      ) : null}
    </Section>
  );
}

/**
 * Facts about the building somebody states once: its name, address and public
 * contact number, where it is on the map, and whether it is sensitive.
 *
 * Stated occupancy was removed here (owner 2026-09-14): it was an admin's guess
 * standing in as the coverage denominator, and a wrong denominator is worse than
 * none. The growth coverage KPI is measured from real enrolment data, not this
 * field. The pin existed as a readiness gap — "the site has no map location" —
 * that the dashboard could name and not fix until this.
 */
function SiteFacts({
  orgId,
  siteId,
  site,
  pinAddress,
}: {
  orgId: string;
  siteId: string;
  site: api.Site | undefined;
  /** What the building's pin reads as after a drag — written into the address field. */
  pinAddress?: { text: string; n: number } | null;
}) {
  const qc = useQueryClient();
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  // Name and address are editable in the detail (owner IA 2026-09-07). Seeded
  // from the site and re-seeded when the selected building changes, so the
  // fields always show the building in front of the admin rather than a stale
  // one from the previous selection.
  const [name, setName] = useState(site?.name ?? '');
  const [address, setAddress] = useState(site?.address ?? '');
  // The sub-premise (migration 0110): unit/suite and level, edited beside the
  // street address they complete.
  const [unit, setUnit] = useState(site?.unit ?? '');
  const [level, setLevel] = useState(site?.level ?? '');
  // The building's public contact number (migration 0107), edited alongside the
  // name and address it sits with in the summary.
  const [phone, setPhone] = useState(site?.phone ?? '');
  useEffect(() => {
    setName(site?.name ?? '');
    setAddress(site?.address ?? '');
    setUnit(site?.unit ?? '');
    setLevel(site?.level ?? '');
    setPhone(site?.phone ?? '');
  }, [site?.id, site?.name, site?.address, site?.unit, site?.level, site?.phone]);
  // The pin was dragged: the address field follows it, unsaved until "Save
  // details" — so what is on file only changes when somebody says so.
  useEffect(() => {
    if (!pinAddress) return;
    setAddress(pinAddress.text);
    setIdentitySaved(false);
  }, [pinAddress]);

  // Remove-building state: a two-step confirm that never uses a browser
  // dialog, and the server's refusal (a building with history) in its words.
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const removeSite = useMutation({
    mutationFn: () => api.deleteSite(orgId, siteId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sites', orgId] });
      // Back to the directory — the building this screen shows no longer
      // exists. pushState + a synthetic popstate is the app's own router.
      window.history.pushState({}, '', '/locations');
      window.dispatchEvent(new PopStateEvent('popstate'));
    },
    onError: (e: Error) => {
      setConfirmRemove(false);
      setRemoveError(e.message);
    },
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['readiness', orgId, siteId] });
    void qc.invalidateQueries({ queryKey: ['sites', orgId] });
  };

  // Success and failure of the name/address save used to look identical (M2).
  // Track both, keyed to the identity save so the other savers on this shared
  // mutation are unaffected; the affordance mirrors ProcedureEditor's — a
  // persistent "Saved" that clears when the fields go dirty again, and a visible
  // error otherwise.
  const [identitySaved, setIdentitySaved] = useState(false);
  const [identityError, setIdentityError] = useState(false);
  const [identityDuplicate, setIdentityDuplicate] = useState<DuplicateSite | null>(null);

  const save = useMutation({
    mutationFn: (body: Parameters<typeof api.updateSite>[2]) => api.updateSite(orgId, siteId, body),
    onSuccess: (_res, body) => {
      refresh();
      if (body && 'name' in body) {
        setIdentitySaved(true);
        setIdentityError(false);
      }
    },
    onError: (e, body) => {
      if (body && 'name' in body) {
        setIdentityError(true);
        // A re-address onto another building is refused in words, never as
        // "try again" — retrying could not succeed.
        setIdentityDuplicate(duplicateFromError(e));
      }
    },
  });

  const identityDirty =
    name.trim() !== (site?.name ?? '') ||
    address !== (site?.address ?? '') ||
    unit !== (site?.unit ?? '') ||
    level !== (site?.level ?? '') ||
    phone !== (site?.phone ?? '');

  return (
    <Section title="Details">
      {/* The building's own name, street address and public contact number.
          `name` is required (it is the point of the row); a blank address or
          phone clears it. */}
      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div className="w-[240px]">
          <Field
            label="Building name"
            value={name}
            onChange={(v) => {
              setIdentitySaved(false);
              setIdentityError(false);
              setName(v);
            }}
            placeholder="Lincoln Primary"
          />
        </div>
        <div className="w-[280px]">
          <Field
            label="Address"
            value={address}
            onChange={(v) => {
              setIdentitySaved(false);
              setIdentityError(false);
              setAddress(v);
            }}
          />
        </div>
        {/* The sub-premise (migration 0110): "Suite 1, Level 8". Blank clears. */}
        <div className="w-[150px]">
          <Field
            label="Unit / Suite"
            value={unit}
            onChange={(v) => {
              setIdentitySaved(false);
              setIdentityError(false);
              setUnit(v);
            }}
            placeholder="Suite 1"
          />
        </div>
        <div className="w-[110px]">
          <Field
            label="Level"
            value={level}
            onChange={(v) => {
              setIdentitySaved(false);
              setIdentityError(false);
              setLevel(v);
            }}
            placeholder="8"
          />
        </div>
        <div className="w-[200px]">
          {/* The number a person rings this building on — the front desk or
              security switchboard. Loose by design (extensions, overseas
              formats), so it is a plain tel input rather than a validated one. */}
          <Field
            label="Phone"
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(v) => {
              setIdentitySaved(false);
              setIdentityError(false);
              setPhone(v);
            }}
            placeholder="(03) 9000 0000"
          />
        </div>
        {/* Distinct visible text from the identical-looking "Save" buttons below
            (L2) — two bare "Save"s in one section are ambiguous by sight, and the
            aria-label already distinguished them. */}
        <Button
          disabled={!name.trim() || !identityDirty || save.isPending}
          label="Save name, address and phone"
          onClick={() => save.mutate({ name: name.trim(), address, unit, level, phone })}
        >
          Save details
        </Button>
        {identitySaved && !identityDirty ? <Status tone="safe">Saved</Status> : null}
      </div>
      {identityError ? (
        <div className="mb-5">
          {identityDuplicate ? (
            <DuplicateSiteFinding site={identityDuplicate} />
          ) : (
            <Finding tone="alert">Couldn&rsquo;t save the details. Try again.</Finding>
          )}
        </div>
      ) : null}
      <div className="border-t border-[var(--line)] pt-5">
        {/* Sensitivity governs cross-org broadcast defaults (outbound silent).
            A refuge is onboarded like any office and reclassified the moment
            somebody realises, so this cannot live only on the create form. */}
        <Button
          label={site?.sensitive ? 'Mark this site not sensitive' : 'Mark this site sensitive'}
          onClick={() => save.mutate({ sensitive: !site?.sensitive })}
        >
          {site?.sensitive ? 'Sensitive — outbound broadcasts stay silent' : 'Mark as sensitive'}
        </Button>
      </div>

      {/* Remove building — the affordance the production wizard test found
          missing (2026-09-25): a building could be created and never taken
          away. Two clicks, both in words: the first states what removal
          means, the second does it. A building with recorded events is
          refused by the server (migration 0018 — its history IS the record)
          and the refusal renders in the server's own words. */}
      <div className="mt-5 border-t border-[var(--line)] pt-5">
        {removeError ? (
          <div className="mb-3">
            <Finding tone="alert">{removeError}</Finding>
          </div>
        ) : null}
        {confirmRemove ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="max-w-[480px] text-[14px] text-[var(--ink-2)]">
              Remove {site?.name ?? 'this building'}? Its assembly points, areas and follower links
              go with it. This cannot be undone.
            </span>
            <Button
              disabled={removeSite.isPending}
              label={`Yes, remove ${site?.name ?? 'this building'}`}
              onClick={() => removeSite.mutate()}
            >
              {removeSite.isPending ? 'Removing…' : 'Yes, remove it'}
            </Button>
            <Button variant="quiet" onClick={() => setConfirmRemove(false)}>
              Keep it
            </Button>
          </div>
        ) : (
          <Button
            variant="quiet"
            label={`Remove ${site?.name ?? 'this building'}`}
            onClick={() => {
              setRemoveError(null);
              setConfirmRemove(true);
            }}
          >
            Remove building
          </Button>
        )}
      </div>

      {site && site.latitude == null ? (
        <div className="mt-5 flex flex-wrap items-end gap-3 border-t border-[var(--line)] pt-5">
          <div className="w-[160px]">
            <Field label="Latitude" value={lat} onChange={setLat} placeholder="-37.7985" />
          </div>
          <div className="w-[160px]">
            <Field label="Longitude" value={lng} onChange={setLng} placeholder="144.9718" />
          </div>
          <Button
            disabled={!lat || !lng || save.isPending}
            label="Place this building on the map"
            onClick={() => save.mutate({ latitude: Number(lat), longitude: Number(lng) })}
          >
            Place on map
          </Button>
        </div>
      ) : null}
    </Section>
  );
}

/**
 * The building's boundary on its own page (migrations 0111 + 0140): a circle
 * ("how far across", stored halved) or a drawn outline — never both, the
 * server clears the other. The outline is drawn on this page's map by tapping
 * corners, with the same `OutlineControls` the wizard's place step uses.
 */
function BoundaryEditor({
  orgId,
  site,
  drawing,
  onDrawing,
}: {
  orgId: string;
  site: api.Site;
  drawing: api.LatLon[] | null;
  onDrawing: (ring: api.LatLon[] | null) => void;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // Switching an outline back to a circle asks for the circle's size first.
  const [circleMode, setCircleMode] = useState(false);
  const [across, setAcross] = useState(
    site.geofence_radius_m != null ? String(site.geofence_radius_m * 2) : '',
  );
  useEffect(() => {
    setAcross(site.geofence_radius_m != null ? String(site.geofence_radius_m * 2) : '');
    setCircleMode(false);
  }, [site.id, site.geofence_radius_m]);
  const save = useMutation({
    mutationFn: (body: {
      boundaryPolygon?: api.LatLon[] | null;
      geofenceRadiusM?: number | null;
    }) => api.updateSite(orgId, site.id, body),
    onSuccess: () => {
      setError(null);
      onDrawing(null);
      setCircleMode(false);
      void qc.invalidateQueries({ queryKey: ['sites', orgId] });
    },
    onError: (e: Error) => {
      const code = (e as { detail?: { code?: unknown } }).detail?.code;
      setError(
        code === 'stale_session'
          ? 'Sign in again to change the boundary — it needs a recent sign-in.'
          : e.message,
      );
    },
  });
  const outline = site.boundary_polygon ?? null;
  const radius = (() => {
    const n = Number(across);
    if (across.trim() === '' || !Number.isFinite(n)) return undefined;
    const r = Math.round(n / 2);
    return r >= 5 && r <= 5000 ? r : undefined;
  })();
  const placed = site.latitude != null && site.longitude != null;
  const finish = () => {
    if (drawing && drawing.length >= 3) save.mutate({ boundaryPolygon: drawing });
  };

  return (
    <div className="mt-4 max-w-[640px]" role="group" aria-label="Building boundary">
      <Label>Boundary</Label>
      {drawing || (outline && !circleMode) ? (
        <OutlineControls
          ring={drawing ? null : outline}
          drawing={drawing}
          canStart={placed}
          pending={save.isPending}
          error={error}
          onStart={() => onDrawing([])}
          onFinish={finish}
          onCancel={() => onDrawing(null)}
          onUseCircle={() => setCircleMode(true)}
        />
      ) : (
        <>
          <div className="mt-2 flex flex-wrap items-end gap-3">
            <div className="w-[160px]">
              <Field
                label="How far across (m)"
                value={across}
                onChange={setAcross}
                placeholder="80"
              />
            </div>
            <Button
              disabled={!placed || radius === undefined || save.isPending}
              label="Save the building's boundary circle"
              onClick={() => save.mutate({ geofenceRadiusM: radius ?? null })}
            >
              {outline ? 'Use this circle' : 'Save'}
            </Button>
            {outline ? (
              <Button variant="quiet" onClick={() => setCircleMode(false)}>
                Keep the outline
              </Button>
            ) : null}
          </div>
          {outline ? (
            error ? (
              <div className="mt-2">
                <Finding>{error}</Finding>
              </div>
            ) : null
          ) : (
            <OutlineControls
              ring={null}
              drawing={null}
              canStart={placed}
              pending={save.isPending}
              error={error}
              onStart={() => onDrawing([])}
              onFinish={finish}
              onCancel={() => onDrawing(null)}
              onUseCircle={() => onDrawing(null)}
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * The evacuation diagrams on the walls, and when each was last checked.
 *
 * AS 3745-2010 requires review at least every five years, and the diagram is
 * the most strategically important object in this product\'s world — the QR
 * strategy lives on it. Reviewing a sign is one action: write today over the
 * old date.
 */
function DiagramRegister({ orgId, siteId }: { orgId: string; siteId: string }) {
  const qc = useQueryClient();
  const [placement, setPlacement] = useState('');

  const diagrams = useQuery({
    queryKey: ['diagrams', orgId, siteId],
    queryFn: () => api.getDiagrams(orgId, siteId),
    enabled: Boolean(siteId),
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['diagrams', orgId, siteId] });
    void qc.invalidateQueries({ queryKey: ['readiness', orgId, siteId] });
  };

  const record = useMutation({
    mutationFn: (p: string) =>
      api.recordDiagram(orgId, siteId, p, new Date().toISOString().slice(0, 10)),
    onSuccess: () => {
      setPlacement('');
      refresh();
    },
  });

  return (
    <Section title="Evacuation diagrams">
      {diagrams.isLoading || !diagrams.data ? (
        <Spinner />
      ) : (
        <>
          {diagrams.data.diagrams.length === 0 ? (
            <Empty>
              No diagrams are recorded. The sign on the wall is the one thing every visitor can
              already read — track when each was last checked.
            </Empty>
          ) : (
            <Table head={['Where it hangs', 'Last reviewed', 'Status', '']}>
              {diagrams.data.diagrams.map((d) => (
                <Row key={d.id}>
                  <Cell wide>{d.placement}</Cell>
                  <Cell>{new Date(d.last_reviewed).toLocaleDateString()}</Cell>
                  <Cell>
                    {d.overdue ? (
                      <Status tone="alert">Overdue</Status>
                    ) : d.due_soon ? (
                      <Status tone="attention">Due within six months</Status>
                    ) : (
                      <Status tone="safe">Current</Status>
                    )}
                  </Cell>
                  <Cell>
                    <Button
                      variant="quiet"
                      label={`Mark ${d.placement} reviewed today`}
                      onClick={() => record.mutate(d.placement)}
                    >
                      Reviewed today
                    </Button>
                  </Cell>
                </Row>
              ))}
            </Table>
          )}

          <div className="mt-5 flex flex-wrap items-end gap-3">
            <div className="w-[320px]">
              <Field
                label="Add a sign"
                value={placement}
                onChange={setPlacement}
                placeholder="Level 2, north stairwell"
              />
            </div>
            <Button
              disabled={!placement || record.isPending}
              label="Record this diagram as reviewed today"
              onClick={() => record.mutate(placement)}
            >
              Record, reviewed today
            </Button>
          </div>
        </>
      )}
    </Section>
  );
}

const EQUIPMENT_CLASS_LABEL: Record<api.EquipmentClass, string> = {
  aed: 'AED',
  first_aid_kit: 'First-aid kit',
  adrenaline_autoinjector: 'Adrenaline autoinjector',
  reliever_inhaler: 'Reliever inhaler',
  oxygen: 'Oxygen',
  evac_chair: 'Evacuation chair',
  spill_kit: 'Spill kit',
  fire_blanket: 'Fire blanket',
  other: 'Other',
};

const EQUIPMENT_ACCESS_LABEL: Record<api.EquipmentAccess, string> = {
  unrestricted: 'Always reachable',
  locked_cabinet: 'Locked cabinet',
  staffed_hours_only: 'Staffed hours only',
  behind_reception: 'Behind reception',
  requires_training: 'Trained retrieval',
};

/** The most severe honesty signal a row carries, worded and toned (E9). */
function equipmentStatus(e: api.Equipment): {
  tone: 'safe' | 'attention' | 'alert';
  label: string;
} {
  if (e.expired) return { tone: 'alert', label: 'Expired' };
  if (e.expiring_soon) return { tone: 'attention', label: 'Expires within a fortnight' };
  if (e.overdue_service) return { tone: 'attention', label: 'Overdue for a service check' };
  if (e.unverified) return { tone: 'attention', label: 'Never checked' };
  return { tone: 'safe', label: 'In date' };
}

const EMPTY_EQUIPMENT: api.EquipmentInput = {
  class: 'aed',
  description: '',
  zoneId: '',
  accessConstraint: 'unrestricted',
  expiryDate: '',
  lastCheckedAt: '',
  placementNote: '',
  notes: '',
};

/**
 * The fixed equipment register (migrations 0069/0072,
 * docs/emergency-equipment.md Tier 1).
 *
 * What the register can and cannot say is the whole point of §6, so the copy
 * holds the line: a row is a record that somebody wrote it down and last
 * checked it, never a claim that the device is present and working now (E9). A
 * lapsed or never-checked row stays in the list and reads as what it is, rather
 * than dropping out and reading as absent. It is a site-config surface, not a
 * retrieval one — the "call 000, lay them flat, do not stand them up" guidance
 * (E5) belongs on the zero-install retrieval page, which is a separate build.
 */
function EquipmentRegister({
  orgId,
  siteId,
  zones,
}: {
  orgId: string;
  siteId: string;
  zones: api.Zone[];
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<api.EquipmentInput>(EMPTY_EQUIPMENT);
  const [error, setError] = useState<string | null>(null);

  const equipment = useQuery({
    queryKey: ['equipment', orgId, siteId],
    queryFn: () => api.getEquipment(orgId, siteId),
    enabled: Boolean(siteId),
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['equipment', orgId, siteId] });
    void qc.invalidateQueries({ queryKey: ['readiness', orgId, siteId] });
  };

  const reset = () => {
    setForm(EMPTY_EQUIPMENT);
    setEditingId(null);
    setOpen(false);
    setError(null);
  };

  // Empty strings are how the form says "not set"; the API wants null.
  const payload = (): api.EquipmentInput => ({
    class: form.class,
    description: form.description.trim(),
    zoneId: form.zoneId || null,
    accessConstraint: form.accessConstraint,
    expiryDate: form.expiryDate || null,
    lastCheckedAt: form.lastCheckedAt || null,
    placementNote: form.placementNote?.trim() || null,
    notes: form.notes?.trim() || null,
  });

  const save = useMutation({
    mutationFn: () =>
      editingId
        ? api.updateEquipment(orgId, siteId, editingId, payload())
        : api.createEquipment(orgId, siteId, payload()),
    onSuccess: () => {
      reset();
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.removeEquipment(orgId, siteId, id),
    onSuccess: refresh,
  });

  const beginEdit = (e: api.Equipment) => {
    setEditingId(e.id);
    setForm({
      class: e.class,
      description: e.description,
      zoneId: e.zone_id ?? '',
      accessConstraint: e.access_constraint,
      expiryDate: e.expiry_date ?? '',
      lastCheckedAt: e.last_checked_at ? e.last_checked_at.slice(0, 10) : '',
      placementNote: e.placement_note ?? '',
      notes: e.notes ?? '',
    });
    setError(null);
    setOpen(true);
  };

  return (
    <Section
      title="Emergency equipment"
      action={
        <Button
          label={open ? 'Cancel adding equipment' : 'Add a piece of equipment'}
          onClick={() => (open ? reset() : setOpen(true))}
        >
          {open ? 'Cancel' : 'Add equipment'}
        </Button>
      }
    >
      {equipment.isLoading || !equipment.data ? (
        <Spinner />
      ) : (
        <>
          {equipment.data.equipment.length === 0 ? (
            <Empty>
              No equipment is recorded. Add the AED, the first-aid kits, the evacuation chair — what
              is here, where it is, and when it was last checked. The register states what was
              entered and assumes nothing else.
            </Empty>
          ) : (
            <Table head={['Type', 'Where it is', 'Access', 'Expiry', 'Last checked', 'Status', '']}>
              {equipment.data.equipment.map((e) => {
                const status = equipmentStatus(e);
                return (
                  <Row key={e.id}>
                    <Cell wide>
                      {EQUIPMENT_CLASS_LABEL[e.class]}
                      <span className="ml-2 text-[13px] text-[var(--ink-2)]">{e.description}</span>
                    </Cell>
                    <Cell>{e.zone_name ?? e.placement_note ?? '—'}</Cell>
                    <Cell>{EQUIPMENT_ACCESS_LABEL[e.access_constraint]}</Cell>
                    <Cell>
                      {e.expiry_date ? new Date(e.expiry_date).toLocaleDateString() : '—'}
                    </Cell>
                    <Cell>
                      {e.last_checked_at ? (
                        new Date(e.last_checked_at).toLocaleDateString()
                      ) : (
                        <span className="text-[var(--ink-2)]">Never</span>
                      )}
                    </Cell>
                    <Cell>
                      <Status tone={status.tone}>{status.label}</Status>
                    </Cell>
                    <Cell>
                      <Button
                        variant="quiet"
                        label={`Edit ${EQUIPMENT_CLASS_LABEL[e.class]} — ${e.description}`}
                        onClick={() => beginEdit(e)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="quiet"
                        label={`Remove ${EQUIPMENT_CLASS_LABEL[e.class]} — ${e.description}`}
                        onClick={() => remove.mutate(e.id)}
                      >
                        Remove
                      </Button>
                    </Cell>
                  </Row>
                );
              })}
            </Table>
          )}

          {open ? (
            <div className="mt-6 max-w-[720px] border-t border-[var(--line)] pt-5">
              <div className="flex flex-wrap gap-3">
                <div className="w-[220px]">
                  <Select
                    label="Type"
                    value={form.class}
                    onChange={(v) => setForm((f) => ({ ...f, class: v as api.EquipmentClass }))}
                    options={(Object.keys(EQUIPMENT_CLASS_LABEL) as api.EquipmentClass[]).map(
                      (c) => ({ value: c, label: EQUIPMENT_CLASS_LABEL[c] }),
                    )}
                  />
                </div>
                <div className="w-[220px]">
                  <Select
                    label="Area"
                    value={form.zoneId ?? ''}
                    onChange={(v) => setForm((f) => ({ ...f, zoneId: v }))}
                    options={[
                      { value: '', label: 'No specific area' },
                      ...zones.map((z) => ({ value: z.id, label: z.name })),
                    ]}
                  />
                </div>
                <div className="w-[220px]">
                  <Select
                    label="Access"
                    value={form.accessConstraint ?? 'unrestricted'}
                    onChange={(v) =>
                      setForm((f) => ({ ...f, accessConstraint: v as api.EquipmentAccess }))
                    }
                    options={(Object.keys(EQUIPMENT_ACCESS_LABEL) as api.EquipmentAccess[]).map(
                      (a) => ({ value: a, label: EQUIPMENT_ACCESS_LABEL[a] }),
                    )}
                  />
                </div>
              </div>

              <div className="mt-4">
                <Field
                  label="Description"
                  value={form.description}
                  onChange={(v) => setForm((f) => ({ ...f, description: v }))}
                  placeholder="AED, wall-mounted by the north lift"
                />
              </div>

              <div className="mt-4 flex flex-wrap gap-3">
                <div className="w-[220px]">
                  <Field
                    label="Expiry date"
                    type="date"
                    value={form.expiryDate ?? ''}
                    onChange={(v) => setForm((f) => ({ ...f, expiryDate: v }))}
                    hint="Pads, batteries or the consumable — the earliest date that matters."
                  />
                </div>
                <div className="w-[220px]">
                  <Field
                    label="Last checked"
                    type="date"
                    value={form.lastCheckedAt ?? ''}
                    onChange={(v) => setForm((f) => ({ ...f, lastCheckedAt: v }))}
                    hint="Left blank reads as never checked, not as fine."
                  />
                </div>
              </div>

              <div className="mt-4">
                <Field
                  label="Note"
                  value={form.notes ?? ''}
                  onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
                  placeholder="Anything a warden should know before relying on it."
                />
              </div>

              {error ? (
                <div className="mt-3">
                  <Finding>{error}</Finding>
                </div>
              ) : null}

              <div className="mt-4">
                <Button
                  variant="primary"
                  disabled={!form.description.trim() || save.isPending}
                  onClick={() => save.mutate()}
                >
                  {editingId ? 'Save changes' : 'Add to register'}
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </Section>
  );
}

/**
 * A zone's name, editable in place.
 *
 * Renaming is the *safe* fix for a mislabelled area — deletion is refused once
 * sweep evidence exists (migration 0017), and the refusal message itself says
 * "rename it instead". Until now the dashboard offered the refusal and not the
 * remedy.
 */
function ZoneName({ orgId, siteId, zone }: { orgId: string; siteId: string; zone: api.Zone }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(zone.name);

  const rename = useMutation({
    mutationFn: () => api.renameZone(orgId, siteId, zone.id, name.trim()),
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['zones', orgId, siteId] });
    },
  });

  if (!editing) {
    return (
      <span className="inline-flex items-center gap-2">
        {zone.name}
        <Button variant="quiet" label={`Rename ${zone.name}`} onClick={() => setEditing(true)}>
          Rename
        </Button>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-2">
      <input
        className="h-8 w-[220px] rounded-[var(--radius-1)] border border-[var(--line-strong)] bg-[var(--paper)] px-2 text-[14px]"
        value={name}
        onChange={(e) => setName(e.target.value)}
        aria-label={`New name for ${zone.name}`}
      />
      <Button
        variant="primary"
        disabled={!name.trim() || rename.isPending}
        label={`Save new name for ${zone.name}`}
        onClick={() => rename.mutate()}
      >
        Save
      </Button>
      <Button variant="quiet" label="Cancel rename" onClick={() => setEditing(false)}>
        Cancel
      </Button>
    </span>
  );
}

/**
 * Whether one building is ready, and the way to fix it if not.
 *
 * Three states, and the third is the one that matters: **while the answer is
 * unknown it says so.** A dash is honest; "Ready" printed before the query
 * answered is the state-asserting bug this product keeps producing, and here it
 * would tell an admin a building is prepared when nothing has been checked.
 *
 * The remedy is a link rather than a button, matching the banner
 * (`design-dashboard.md` §7c): the fix belongs where the problem is stated, and
 * a button in a table cell claims more weight than the row around it.
 */
function SiteStatus({
  orgId,
  siteId,
  onReview,
}: {
  orgId: string;
  siteId: string;
  onReview: () => void;
}) {
  const readiness = useQuery({
    queryKey: ['readiness', orgId, siteId],
    queryFn: () => api.getReadiness(orgId, siteId),
  });

  if (!readiness.data) return <span className="text-[var(--ink-2)]">—</span>;

  const { ready, gaps } = readiness.data;
  if (ready) return <Status tone="safe">Ready</Status>;

  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      <Status tone="attention">
        {gaps.length} {gaps.length === 1 ? 'gap' : 'gaps'}
      </Status>
      <InlineAction onClick={onReview}>Review</InlineAction>
    </span>
  );
}

/**
 * The buildings-list container: a titled `<Section>` (hairline + heading) for
 * the standalone/Overview caller, or a bare fragment when `bare` — Locations
 * drives the list edge-to-edge under the shared `Directory` header, so a second
 * "Buildings" title and a boxed frame would be redundant (owner fix
 * 2026-09-07).
 */
function ListShell({
  bare,
  title,
  children,
}: {
  bare: boolean;
  title: string;
  children: ReactNode;
}) {
  return bare ? <>{children}</> : <Section title={title}>{children}</Section>;
}

export function Sites({
  orgId,
  sites,
  siteId,
  onSite,
  onReview,
  onSetup,
  onRaise,
  onPeople,
  view,
  hideTitle = false,
  hideAddForm = false,
  bareList = false,
  search,
  onCreated,
}: {
  orgId: string;
  sites: api.Site[];
  siteId: string;
  onSite: (id: string) => void;
  /** Where a building's gaps are actually listed. */
  onReview: () => void;
  /**
   * Open the People screen filtered to this building — the detail body's "Alert
   * audience" drill-down to the named list, reusing People-filtered-by-site
   * rather than duplicating a roster table. Omitted on the list/add views.
   */
  onPeople?: (siteId: string) => void;
  /**
   * Into the raise/drill flow for the selected building — the summary header's
   * "Raise / run a drill". Reuses the dashboard's existing raise entry (the
   * chrome's "Raise an alert", which opens the Activate screen against the
   * app-wide selected site); the building page is already open for that site,
   * so navigating is enough. Omitted on the list/add views, which show no
   * summary header. */
  onRaise?: () => void;
  /**
   * Into the guided wizard, for an org that has no buildings yet. Location
   * management is the configure surface (reached from Settings → Locations),
   * and its zero state is the wizard rather than a bare add form — the same
   * "zero locations → the wizard" rule the first-run gate and Home follow.
   */
  onSetup?: () => void;
  /**
   * Which half of the surface to render, for the Locations list → detail split
   * (owner IA 2026-09-07). `'list'` renders only the buildings directory
   * (search, add, share, zero-state wizard); `'detail'` renders only the
   * selected building's sections, so the caller drives selection from its own
   * list and gives the detail its own header. Required — both real callers
   * (`locations.tsx`) pass one explicitly, and there is no combined layout the
   * product ships.
   */
  view: 'list' | 'detail' | 'add';
  /**
   * Suppress the list view's own `Locations` PageTitle. The shared
   * rail-directory shell (`directory.tsx`) carries the screen title in its
   * sticky header now, so `locations.tsx` sets this to avoid a second title
   * above the buildings list. Additive and default-off, so the
   * Overview/readiness caller is untouched.
   */
  hideTitle?: boolean;
  /**
   * Suppress the loose inline "Add building" form in the list view. The primary
   * CTA now opens the add form in the right inspect overlay (F, owner correction
   * 2026-09-07), rendered as `view="add"`, so the list itself carries no inline
   * form. Additive and default-off.
   */
  hideAddForm?: boolean;
  /**
   * Render the buildings list **edge-to-edge**, the way People does (owner fix
   * 2026-09-07): no inner `<Section title="Buildings">`, no boxed
   * `max-h`/`overflow` scroll wrapper, and no in-body search field — the search
   * moves up into the shared `Directory` header (the `search` prop below drives
   * filtering). The whole content region scrolls under the sticky header, and
   * the column labels stay put (`stickyHeader`). Additive and default-off, so
   * the standalone/Overview caller keeps the boxed section it has.
   */
  bareList?: boolean;
  /**
   * The search string, when the list is `bareList` and the header owns the
   * input. Filters the table (name/address) exactly as the in-body box did;
   * ignored when `bareList` is off (the internal box drives it then).
   */
  search?: string;
  /** Called after a building is created (used to close the add overlay). */
  onCreated?: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  // The coordinate MapKit hands back when a suggestion is picked. Sent to
  // the server on create so the sites list has a pin from the moment the
  // row exists, and rendered as a preview below the input by the
  // AddressAutocomplete component so the person adding the building can
  // see the pin land on the right spot before saving. Cleared with the
  // field.
  const [pickedCoord, setPickedCoord] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [zone, setZone] = useState('');
  /**
   * Coordinates and the sensitive flag: the two things a site can only get
   * at creation. Latitude/longitude place the pin on the live-view map (and
   * on the sweep-board picker for zones), and there is no update route to
   * set them later; `sensitive` refuses the public follow door and is the
   * one signal that keeps a courthouse or a refuge out of `discover`. Both
   * hidden behind an "Advanced" expander so the common café case still asks
   * two questions.
   */
  const [advanced, setAdvanced] = useState(false);
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [sensitive, setSensitive] = useState(false);
  const [siteError, setSiteError] = useState<string | null>(null);
  // One building, one site: the address or pin may already be one of this
  // org's buildings (warned early), or a race may beat the warning (the 409).
  const existingSite = useSiteMatch(orgId, { address, coord: pickedCoord });
  const [racedSite, setRacedSite] = useState<DuplicateSite | null>(null);
  useEffect(() => {
    setRacedSite(null);
  }, [address, pickedCoord]);
  const duplicateSite = existingSite ?? racedSite;
  // The buildings list must scale to an org with hundreds of buildings (owner
  // review 2026-09-07): a search box narrows it and the list scrolls within its
  // own bound. It filters the table only; the selected `site`, the map and the
  // share card still read from the full list.
  const [buildingSearch, setBuildingSearch] = useState('');

  const zones = useQuery({
    queryKey: ['zones', orgId, siteId],
    queryFn: () => api.getZones(orgId, siteId),
    enabled: Boolean(siteId),
  });

  const points = useQuery({
    queryKey: ['points', orgId, siteId],
    queryFn: () => api.getAssemblyPoints(orgId, siteId),
    enabled: Boolean(siteId),
  });

  const createSite = useMutation({
    mutationFn: (opts?: { confirmDistinct?: boolean }) => {
      // Who can raise a real alert here is chosen on the building's Wardens
      // section, not at creation (product owner, 2026-09-30). The server
      // stores one administrator, unchosen, and readiness asks.
      const extras: api.CreateSiteExtras = { sensitive };
      if (opts?.confirmDistinct) extras.confirmDistinct = true;
      // A coordinate can arrive two ways: MapKit hands one back when the person
      // picks an address suggestion (pickedCoord), or they type one into the
      // Advanced fields. Prefer the picked coordinate; otherwise fall back to
      // the manual pair. Both or neither for the manual pair — a site with one
      // coordinate is not a location, matching the API's own refusal
      // (api/src/routes/orgs.ts:247).
      if (pickedCoord) {
        extras.latitude = pickedCoord.latitude;
        extras.longitude = pickedCoord.longitude;
      } else {
        const lat = latitude.trim();
        const lon = longitude.trim();
        if (lat && lon) {
          const latN = Number(lat);
          const lonN = Number(lon);
          if (
            !Number.isFinite(latN) ||
            !Number.isFinite(lonN) ||
            latN < -90 ||
            latN > 90 ||
            lonN < -180 ||
            lonN > 180
          ) {
            throw new Error('Coordinates must be numbers within the world.');
          }
          extras.latitude = latN;
          extras.longitude = lonN;
        } else if (lat || lon) {
          throw new Error('Latitude and longitude go together, or leave both blank.');
        }
      }
      return api.createSite(orgId, name, address, extras);
    },
    onSuccess: () => {
      setName('');
      setAddress('');
      setPickedCoord(null);
      setLatitude('');
      setLongitude('');
      setSensitive(false);
      setAdvanced(false);
      setSiteError(null);
      void qc.invalidateQueries({ queryKey: ['sites'] });
      onCreated?.();
    },
    onError: (e: Error) => {
      const dup = duplicateFromError(e);
      setRacedSite(dup);
      const code = (e as { detail?: { code?: unknown } }).detail?.code;
      setSiteError(
        dup
          ? null
          : code === 'stale_session'
            ? 'Sign in again to add this building — it needs a recent sign-in.'
            : e.message,
      );
    },
  });

  const createZone = useMutation({
    mutationFn: () => api.addZone(orgId, siteId, zone),
    onSuccess: () => {
      setZone('');
      void qc.invalidateQueries({ queryKey: ['zones', orgId, siteId] });
    },
  });

  const dropZone = useMutation({
    mutationFn: (id: string) => api.removeZone(orgId, siteId, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['zones', orgId, siteId] }),
  });

  const site = sites.find((s) => s.id === siteId);

  // The Wardens (ECO) board discloses like the standalone Locations board did
  // (it moved in here from locations.tsx so it can sit in the building order
  // rather than bolted beneath everything): hidden on `first-warden` — a solo
  // café owner is implicitly Chief — and appearing the moment `eco_assignments`
  // gains a row. Phase 1 evaluates `true`, so it renders as it did before.
  const disclosure = useDisclosure(orgId, siteId || undefined);
  // After the activation rule saves: the building row carries the rule and
  // readiness carries the "choose who can raise a real alert" gap.
  const refreshSite = () => {
    void qc.invalidateQueries({ queryKey: ['readiness', orgId, siteId] });
    void qc.invalidateQueries({ queryKey: ['sites', orgId] });
  };

  /**
   * Zone placement: pick an area, then click the map where its centre is.
   *
   * Migration 0029 gave zones an optional centre so the sweep board could be
   * drawn, and until this control existed nothing could set it — the live map
   * has shown pins for exactly zero zones since. One click, undoable, and an
   * unplaced zone is a truthful state rather than an error.
   */
  const [placingZoneId, setPlacingZoneId] = useState<string | null>(null);
  const placeZone = useMutation({
    mutationFn: (p: { zoneId: string; latitude: number | null; longitude: number | null }) =>
      api.placeZone(orgId, siteId, p.zoneId, p.latitude, p.longitude),
    onSuccess: () => {
      setPlacingZoneId(null);
      void qc.invalidateQueries({ queryKey: ['zones', orgId, siteId] });
    },
  });

  /**
   * The site's own pin. There is no map-click add for sites at creation (the
   * form is on the same screen but above the map), so this control catches
   * the buildings that were created before coordinates were an option — a
   * one-click place, or a nudge to move.
   */
  const [placingSite, setPlacingSite] = useState(false);
  // The building's drawn outline in progress (migration 0140): while set, map
  // clicks add corners instead of placing anything else.
  const [drawingOutline, setDrawingOutline] = useState<api.LatLon[] | null>(null);
  useEffect(() => setDrawingOutline(null), [siteId]);
  // What the building's pin reads as after it is dragged — written into the
  // Details address field (unsaved until "Save details"), so the field follows
  // the pin (Trav, 2026-10-01). `n` makes a repeat reading a new event.
  const [pinAddress, setPinAddress] = useState<{ text: string; n: number } | null>(null);
  useEffect(() => setPinAddress(null), [siteId]);
  const placeSite = useMutation({
    mutationFn: (p: { latitude: number; longitude: number; dragged?: boolean }) =>
      api.updateSite(orgId, siteId, { latitude: p.latitude, longitude: p.longitude }),
    onSuccess: (_r, p) => {
      setPlacingSite(false);
      void qc.invalidateQueries({ queryKey: ['sites', orgId] });
      if (p.dragged)
        void reverseAddress(p.latitude, p.longitude).then((text) => {
          if (text) setPinAddress((prev) => ({ text, n: (prev?.n ?? 0) + 1 }));
        });
    },
    // A pin dropped onto another building is refused (409 site_duplicate);
    // the line above the map shows the server's sentence rather than silence,
    // and the pin goes back to where the server has it.
  });

  /**
   * Assembly-point add: name + kind, then click on the map. Same pattern as
   * placeZone (which was the finding that made zones renderable on the sweep
   * board — a form without a click was two forms; a click without a form was
   * a nameless pin). Once the caller commits `placingAssembly`, the next map
   * click creates the point at those coordinates and clears the form.
   */
  const [placingAssembly, setPlacingAssembly] = useState<{
    name: string;
    kind: api.AssemblyKind;
  } | null>(null);
  const [newApName, setNewApName] = useState('');
  const [newApKind, setNewApKind] = useState<api.AssemblyKind>('outdoor_assembly');
  const addAssembly = useMutation({
    mutationFn: (p: { latitude: number; longitude: number }) => {
      const pending = placingAssembly!;
      return api.addAssemblyPoint(
        orgId,
        siteId,
        pending.name,
        p.latitude,
        p.longitude,
        pending.kind,
      );
    },
    onSuccess: () => {
      setPlacingAssembly(null);
      setNewApName('');
      setNewApKind('outdoor_assembly');
      void qc.invalidateQueries({ queryKey: ['points', orgId, siteId] });
    },
  });

  const mapPoints: MapPoint[] = [];
  if (site?.latitude != null && site.longitude != null) {
    mapPoints.push({
      id: site.id,
      latitude: site.latitude,
      longitude: site.longitude,
      label: site.name,
      kind: 'site',
      // Press and drag to correct where the building is — not while the map
      // is busy placing or drawing something else.
      draggable: !drawingOutline && !placingZoneId && !placingAssembly,
    });
  }
  for (const p of points.data?.assemblyPoints ?? []) {
    mapPoints.push({
      id: p.id,
      latitude: p.latitude,
      longitude: p.longitude,
      label: p.name,
      kind: 'assembly',
    });
  }
  for (const z of zones.data?.zones ?? []) {
    if (z.latitude == null || z.longitude == null) continue;
    mapPoints.push({
      id: z.id,
      latitude: z.latitude,
      longitude: z.longitude,
      label: z.name,
      // On the readiness screen an area has no sweep state; it is simply not
      // started until an event exists.
      kind: 'unswept',
    });
  }

  // The add-building form, shared by the list view's inline slot (when not
  // suppressed) and the `view="add"` overlay (F, owner correction 2026-09-07).
  const addBuildingForm = (
    <div className="mt-5">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-[240px]">
          <Field
            label="New building"
            value={name}
            onChange={setName}
            placeholder="Lincoln Primary"
          />
        </div>
        <div className="w-[380px]">
          {/* MapKit JS autocomplete. Same engine the dashboard's maps use so
              no new vendor and no CSP change. When a suggestion is picked it
              hands back a coordinate (pickedCoord), so the building has a pin
              from the moment the row exists with no second geocode. Falls back
              to a plain input when MapKit is unavailable — an address typed by
              hand still saves, and the Advanced coordinates still apply. */}
          <AddressAutocomplete
            label="Address"
            value={address}
            onChange={setAddress}
            onPick={(coord) => setPickedCoord(coord)}
            // Look near the org's own buildings first — a bias, never a fence.
            region={addressSearchRegion({ buildings: sites, near: nearMe() })}
            offerMyLocation
          />
        </div>
        <Button
          variant="primary"
          disabled={!name || createSite.isPending || duplicateSite !== null}
          onClick={() => createSite.mutate({})}
        >
          Add building
        </Button>
      </div>
      {duplicateSite ? (
        <p className="mt-2 text-[13px] text-[var(--ink-2)]">
          This building is already set up — open it, or confirm it&rsquo;s a different building.
        </p>
      ) : null}

      <div className="mt-3">
        <Button
          variant="quiet"
          onClick={() => setAdvanced((v) => !v)}
          label={advanced ? 'Hide advanced options' : 'Show advanced options'}
        >
          {advanced
            ? 'Hide coordinates and sensitive setting'
            : 'Coordinates and sensitive setting'}
        </Button>
      </div>

      {advanced ? (
        <div className="mt-3 rounded-[var(--radius-1)] border border-[var(--line)] bg-[var(--paper-2)] p-4">
          {/*
                No update route sets these later, and no map-place UI for sites
                exists yet — a site created with no coordinates is invisible on
                the live view map, and a public building created with `sensitive`
                left unchecked is discoverable through the public follow door.
                Both are corrected only by re-creating the site, so ask at
                creation rather than pretending they can be fixed after.
              */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-[160px]">
              <Field
                label="Latitude"
                value={latitude}
                onChange={setLatitude}
                placeholder="-37.8043"
                inputMode="numeric"
                hint="Right-click a place in Google Maps and copy the number."
              />
            </div>
            <div className="w-[160px]">
              <Field
                label="Longitude"
                value={longitude}
                onChange={setLongitude}
                placeholder="144.9723"
                inputMode="numeric"
              />
            </div>
          </div>

          <label className="mt-4 flex items-start gap-2 text-[14px]">
            <input
              type="checkbox"
              checked={sensitive}
              onChange={(e) => setSensitive(e.target.checked)}
              className="mt-1"
            />
            <span>
              <span className="text-[var(--ink)]">Sensitive site.</span>{' '}
              <span className="text-[var(--ink-2)]">
                No public follow link, hidden from Discover, silent-by-default cross-org. Right for
                refuges, courthouses, custodial sites and family violence services.
              </span>
            </span>
          </label>
        </div>
      ) : null}

      {duplicateSite ? (
        <div className="mt-3 max-w-[640px]">
          <DuplicateSiteFinding
            site={duplicateSite}
            confirming={createSite.isPending}
            // Never greyed by an unnamed prerequisite: it creates, or it says
            // what is missing (Trav, 2026-09-30: "it does nothing").
            onConfirmDistinct={() => {
              if (!name.trim()) {
                setSiteError(
                  'Name the building first — then it can be created beside the existing one.',
                );
                return;
              }
              setSiteError(null);
              createSite.mutate({ confirmDistinct: true });
            }}
          />
        </div>
      ) : null}
      {siteError ? <p className="mt-2 text-[13px] text-[var(--attention)]">{siteError}</p> : null}
    </div>
  );

  // The add form on its own, for the right inspect overlay (F). Rendered with no
  // list or detail around it — the overlay supplies the chrome.
  if (view === 'add') {
    return <>{addBuildingForm}</>;
  }

  // Zero locations → the wizard, not a bare add form. Adding the very first
  // building means creating its areas and assembly points too, which is exactly
  // what the guided flow walks; the loose form here is for the second building
  // and after (Trav's IA call — Settings configures, the wizard onboards).
  if (view === 'list' && sites.length === 0) {
    return (
      <>
        {hideTitle ? null : (
          <PageTitle sub="Buildings, the areas inside them, where people are sent, and the equipment in them.">
            Locations
          </PageTitle>
        )}
        <Empty
          action={
            onSetup ? (
              <Button variant="primary" onClick={onSetup}>
                Set up your first location
              </Button>
            ) : undefined
          }
        >
          No buildings yet. Set up your first location to add a building, place it on the map, and
          record its areas and assembly points.
        </Empty>
      </>
    );
  }

  return (
    <>
      {view === 'list' ? (
        <>
          {hideTitle ? null : (
            <PageTitle sub="Buildings, the areas inside them, where people are sent, and the equipment in them.">
              Locations
            </PageTitle>
          )}

          <ListShell bare={bareList} title="Buildings">
            {/*
          The state of each building, in the row that names it
          (`design-dashboard.md` §7c). Apple Business Manager's locations table
          reads *"○ Incomplete · Verify Now ›"* in the Status cell rather than
          making somebody open each place to find out, and the reason carries
          here: an admin with six buildings should be able to see which one is
          not ready without reading six cards.

          The in-body search box is only for the standalone/Overview caller; in
          `bareList` mode (Locations) the search lives in the shared header and
          arrives via the `search` prop.
        */}
            {!bareList && sites.length > 1 ? (
              <div className="mb-3 max-w-[320px]">
                <Field
                  label="Search buildings"
                  value={buildingSearch}
                  onChange={setBuildingSearch}
                  placeholder="Name or address"
                />
              </div>
            ) : null}
            {(() => {
              const q = (bareList ? (search ?? '') : buildingSearch).trim().toLowerCase();
              const shownSites = q
                ? sites.filter((s) => `${s.name} ${s.address ?? ''}`.toLowerCase().includes(q))
                : sites;
              if (shownSites.length === 0) {
                return (
                  <Empty>
                    Nothing matches “{bareList ? search : buildingSearch}”. Clear the search to see
                    every building.
                  </Empty>
                );
              }
              const table = (
                <Table
                  stickyHeader={bareList}
                  head={[
                    'Name',
                    'Status',
                    'Address',
                    // Plainer than "Followers", with the meaning a hover away (L1).
                    <span key="following" title="People subscribed to this building's alerts">
                      Following
                    </span>,
                    '',
                  ]}
                >
                  {shownSites.map((s) => (
                    <Row key={s.id} active={s.id === siteId}>
                      <Cell>
                        <span className="flex items-center gap-2.5">
                          {/* Leading building glyph in currentColor — themes
                                  with the row; the name carries the meaning. */}
                          <span aria-hidden className="shrink-0 text-[var(--ink-2)]">
                            <BuildingIcon size={17} />
                          </span>
                          {s.name}
                        </span>
                      </Cell>
                      <Cell>
                        <SiteStatus orgId={orgId} siteId={s.id} onReview={onReview} />
                      </Cell>
                      <Cell wide>
                        {s.address ?? <span className="text-[var(--ink-2)]">—</span>}
                      </Cell>
                      {/* The follow graph is the sales pipeline (growth-triggers.md):
                  a waiting crowd at a building is the signal, so the number
                  lives where an admin already looks. */}
                      <Cell numeric>{s.followers}</Cell>
                      <Cell>
                        <span className="inline-flex flex-wrap items-center gap-2">
                          <Button
                            variant="quiet"
                            label={`Select ${s.name}`}
                            onClick={() => onSite(s.id)}
                          >
                            {s.id === siteId ? 'Selected' : 'Select'}
                          </Button>
                          {/*
                    The site was created before coordinates were an option
                    or the admin skipped the advanced fields — the map below
                    is blank for it. One click starts placing, the next click
                    on the map writes the coordinates back through
                    `updateSite`. Only appears for the selected site, so
                    the map that receives the click is the right one.
                  */}
                          {s.id === siteId && (s.latitude == null || s.longitude == null) ? (
                            placingSite ? (
                              <Button
                                variant="quiet"
                                label={`Cancel placing ${s.name}`}
                                onClick={() => setPlacingSite(false)}
                              >
                                Cancel
                              </Button>
                            ) : (
                              <Button
                                variant="secondary"
                                label={`Place ${s.name} on the map`}
                                onClick={() => setPlacingSite(true)}
                              >
                                Place on map
                              </Button>
                            )
                          ) : null}
                        </span>
                      </Cell>
                    </Row>
                  ))}
                </Table>
              );
              // Edge-to-edge for Locations; a bounded scroll box for the
              // standalone/Overview caller (so hundreds of buildings never dump
              // the whole list, and the header row stays put as it scrolls). In
              // the Locations rail directory the list is bled to the sheet edges
              // (FullBleedList), matching People and Teams so the rail lists read
              // as one edge-to-edge surface under the full-width header band.
              return bareList ? (
                <FullBleedList>{table}</FullBleedList>
              ) : (
                <div className="max-h-[60vh] overflow-y-auto rounded-[var(--radius-2)] border border-[var(--glass-edge)]">
                  <div className="px-4 pt-1 pb-4">{table}</div>
                </div>
              );
            })()}

            {hideAddForm ? null : addBuildingForm}
          </ListShell>
        </>
      ) : null}

      {view === 'detail' && siteId ? (
        <>
          {/* The building read at a glance, then the sections that edit it in a
              deliberate order (owner redesign): summary → details → areas →
              assembly → procedures → wardens → equipment → followers →
              diagrams/PEEP. */}
          <SiteSummaryHeader orgId={orgId} siteId={siteId} site={site} onRaise={onRaise} />

          <SiteFacts orgId={orgId} siteId={siteId} site={site} pinAddress={pinAddress} />

          <AlertAudience orgId={orgId} siteId={siteId} site={site} onPeople={onPeople} />

          <Section title="Areas and floors">
            {zones.isLoading || !zones.data ? (
              <Spinner />
            ) : zones.data.zones.length === 0 ? (
              <Empty>
                No areas are defined, so an area sweep is not possible. Individual confirmation
                alone misses every visitor and anybody without a phone.
              </Empty>
            ) : (
              <Table head={['Area', 'Wardens', 'On map', '']}>
                {zones.data.zones.map((z) => (
                  <Row key={z.id}>
                    <Cell wide>
                      <ZoneName orgId={orgId} siteId={siteId} zone={z} />
                    </Cell>
                    <Cell numeric>{z.wardens_assigned}</Cell>
                    <Cell>
                      {placingZoneId === z.id ? (
                        <Status tone="attention">Click the map below</Status>
                      ) : z.latitude != null ? (
                        <Button
                          variant="quiet"
                          label={`Take ${z.name} off the map`}
                          onClick={() =>
                            placeZone.mutate({ zoneId: z.id, latitude: null, longitude: null })
                          }
                        >
                          Placed — clear
                        </Button>
                      ) : (
                        <Button
                          variant="quiet"
                          label={`Place ${z.name} on the map`}
                          onClick={() => setPlacingZoneId(z.id)}
                        >
                          Place
                        </Button>
                      )}
                    </Cell>
                    <Cell>
                      <Button
                        variant="quiet"
                        label={`Remove area ${z.name}`}
                        onClick={() => dropZone.mutate(z.id)}
                      >
                        Remove
                      </Button>
                    </Cell>
                  </Row>
                ))}
              </Table>
            )}

            <div className="mt-5 flex items-end gap-3">
              <div className="w-[280px]">
                <Field
                  label="New area"
                  value={zone}
                  onChange={setZone}
                  placeholder="Level 3 East"
                />
              </div>
              <Button disabled={!zone || createZone.isPending} onClick={() => createZone.mutate()}>
                Add area
              </Button>
            </div>
          </Section>

          {/*
            Assembly points as a map, not as a pair of decimal columns.

            The question anybody actually has about a muster point is "where is
            it relative to the building" — is it across the road, is it on the
            side the smoke is on, is it far enough away. A latitude answers none
            of that. This screen used to print `-37.80123` and call it done.
          */}
          <Section title="Assembly points">
            {points.isLoading || !points.data ? (
              <Spinner />
            ) : (
              <>
                {placingZoneId ? (
                  <p className="mb-2 text-[14px] text-[var(--attention)]">
                    Click where the centre of{' '}
                    <strong>{zones.data?.zones.find((z) => z.id === placingZoneId)?.name}</strong>{' '}
                    is.
                  </p>
                ) : null}
                {placingSite ? (
                  <p className="mb-2 text-[14px] text-[var(--attention)]">
                    Click where <strong>{site?.name}</strong> is on the map.
                  </p>
                ) : null}
                {placeSite.error ? (
                  <p role="status" className="mb-2 text-[14px] text-[var(--attention)]">
                    {placeSite.error.message}
                  </p>
                ) : null}
                {placingAssembly ? (
                  <p className="mb-2 text-[14px] text-[var(--attention)]">
                    Click where <strong>{placingAssembly.name}</strong> is.
                  </p>
                ) : null}
                <Map
                  points={
                    drawingOutline
                      ? [
                          ...mapPoints,
                          ...drawingShape(drawingOutline, 'outline-drawing', 'boundary', true)
                            .points,
                        ]
                      : mapPoints
                  }
                  height={360}
                  // The building's boundary — its circle or its drawn outline
                  // (0140), and the outline being drawn from its third corner.
                  circles={
                    site?.latitude != null &&
                    site.longitude != null &&
                    site.geofence_radius_m != null &&
                    !drawingOutline
                      ? [
                          {
                            id: `boundary-${site.id}`,
                            latitude: site.latitude,
                            longitude: site.longitude,
                            radiusM: site.geofence_radius_m,
                            kind: 'boundary',
                          },
                        ]
                      : []
                  }
                  polygons={
                    drawingOutline
                      ? // Every corner from the first tap, a line from the second.
                        drawingShape(drawingOutline, 'outline-drawing', 'boundary').polygons
                      : site?.boundary_polygon
                        ? [
                            {
                              id: `outline-${site.id}`,
                              ring: site.boundary_polygon,
                              kind: 'boundary',
                            },
                          ]
                        : []
                  }
                  onDragEnd={(pointId, latitude, longitude) => {
                    if (site && pointId === site.id) {
                      placeSite.mutate({ latitude, longitude, dragged: true });
                      return;
                    }
                    const corner = cornerIndex(pointId, 'outline-drawing');
                    if (corner !== null && drawingOutline)
                      setDrawingOutline(
                        drawingOutline.map((c, i) => (i === corner ? { latitude, longitude } : c)),
                      );
                  }}
                  onClick={
                    drawingOutline
                      ? (latitude, longitude) =>
                          drawingOutline.length < 64 &&
                          setDrawingOutline([...drawingOutline, { latitude, longitude }])
                      : placingZoneId
                        ? (latitude, longitude) =>
                            placeZone.mutate({ zoneId: placingZoneId, latitude, longitude })
                        : placingSite
                          ? (latitude, longitude) => placeSite.mutate({ latitude, longitude })
                          : placingAssembly
                            ? (latitude, longitude) => addAssembly.mutate({ latitude, longitude })
                            : undefined
                  }
                />
                <MapKey
                  kinds={[...new Set(mapPoints.map((m) => m.kind))].filter((k) => k !== 'vertex')}
                />
                {site ? (
                  <BoundaryEditor
                    orgId={orgId}
                    site={site}
                    drawing={drawingOutline}
                    onDrawing={setDrawingOutline}
                  />
                ) : null}

                {/*
                  Add an assembly point: name + kind, then click on the map.
                  Same pattern as placing a zone (0029) — a form-then-click
                  flow because a form alone gives a nameless coordinate and a
                  click alone gives no coordinate. Rendered inline whether or
                  not points exist, so the same interaction adds the first
                  and the tenth.
                */}
                <div className="mt-5 rounded-[var(--radius-1)] border border-[var(--line)] bg-[var(--paper-2)] p-4">
                  {points.data.assemblyPoints.length === 0 ? (
                    <p className="mb-3 text-[14px] text-[var(--ink-2)]">
                      No assembly point is set — the single most useful fact a person needs during
                      an evacuation. Name it, pick what it is used for, then click on the map.
                    </p>
                  ) : null}
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="w-[220px]">
                      <Field
                        label="Assembly point name"
                        value={newApName}
                        onChange={setNewApName}
                        placeholder="Front car park"
                      />
                    </div>
                    <div className="w-[220px]">
                      <Select
                        label="Used for"
                        value={newApKind}
                        onChange={(v) => setNewApKind(v as api.AssemblyKind)}
                        options={(
                          [
                            'outdoor_assembly',
                            'indoor_shelter',
                            'reunification',
                          ] as api.AssemblyKind[]
                        ).map((k) => ({ value: k, label: ASSEMBLY_KIND_LABEL[k] }))}
                      />
                    </div>
                    {placingAssembly ? (
                      <Button
                        variant="quiet"
                        onClick={() => setPlacingAssembly(null)}
                        label="Cancel placing this assembly point"
                      >
                        Cancel
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        disabled={!newApName.trim()}
                        onClick={() =>
                          setPlacingAssembly({ name: newApName.trim(), kind: newApKind })
                        }
                        label={`Place ${newApName || 'this assembly point'} on the map`}
                      >
                        Place on map
                      </Button>
                    )}
                  </div>
                </div>

                {points.data.assemblyPoints.length === 0 ? null : (
                  <div className="mt-5">
                    {/*
                      Step-free access is three columns' worth of meaning in
                      one: yes, no, and nobody has looked. "Not checked" is
                      shown as attention rather than as a blank, because a blank
                      reads as "fine" and this is the most common state a real
                      site is in.
                    */}
                    <Table
                      head={['Name', 'Used for', 'Distance', 'Holds', 'Step-free', 'Instructions']}
                    >
                      {points.data.assemblyPoints.map((p) => (
                        <Row key={p.id}>
                          <Cell wide>
                            {p.name}
                            {p.is_default ? (
                              <span className="mono ml-2 text-[11px] text-[var(--ink-2)]">
                                DEFAULT
                              </span>
                            ) : null}
                          </Cell>
                          <Cell>{ASSEMBLY_KIND_LABEL[p.kind]}</Cell>
                          <Cell numeric>
                            {site?.latitude != null && site.longitude != null
                              ? `${metresBetween(site.latitude, site.longitude, p.latitude, p.longitude)} m`
                              : '—'}
                          </Cell>
                          <Cell numeric>{p.capacity ?? '—'}</Cell>
                          <Cell>
                            {p.step_free_access === true ? (
                              <Status tone="safe">Yes</Status>
                            ) : p.step_free_access === false ? (
                              <Status tone="alert">No</Status>
                            ) : (
                              <Status tone="attention">Not checked</Status>
                            )}
                          </Cell>
                          <Cell>
                            <ProfileCoverage instructions={p.instructions} />
                          </Cell>
                        </Row>
                      ))}
                    </Table>

                    <div className="mt-5">
                      <PointEditor
                        orgId={orgId}
                        siteId={siteId}
                        points={points.data.assemblyPoints}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </Section>

          <ProcedureEditor orgId={orgId} siteId={siteId} />

          {/* Wardens (ECO) — who sweeps where (AS 3745). It lives with the
              building it is about, in the building order rather than bolted at
              the very bottom; hidden until the org has wardens (disclosure). The
              embedded Eco carries its own <h3> and description under this <h2>. */}
          {/* Who can raise a real alert here sits with the wardens (product
              owner, 2026-09-30), so the section always renders — a building
              with no wardens yet still has to answer it, and readiness asks.
              The warden board itself stays behind disclosure. */}
          <Section title="Wardens">
            <ActivationRuleSetting
              orgId={orgId}
              siteId={siteId}
              site={site}
              onSaved={refreshSite}
            />
            {disclosure.isVisible('ecoScreen') ? (
              <div className="mt-6 border-t border-[var(--line)] pt-5">
                <Eco orgId={orgId} sites={sites} siteId={siteId} onSite={onSite} embedded />
              </div>
            ) : null}
          </Section>

          <EquipmentRegister orgId={orgId} siteId={siteId} zones={zones.data?.zones ?? []} />

          {/* The building's QR codes — follow this place and visitor
              sign-in — each with its purpose, an A4 poster and an image
              (docs/qr-codes.md). The links come from the API, never built
              here; a sensitive building gets no follow code. */}
          <QrCodes orgId={orgId} siteId={siteId} followers={site?.followers} />

          <DiagramRegister orgId={orgId} siteId={siteId} />

          <PeepRegister orgId={orgId} siteId={siteId} />
        </>
      ) : null}
    </>
  );
}

/* --------------------------------------------------------------------- ECO */

const ECO_ROLES = [
  'chief_warden',
  'deputy_chief_warden',
  'communications_officer',
  'area_warden',
  'floor_warden',
  'warden',
  'first_aid_officer',
  'emergency_coordinator',
];

export const humanRole = (r: string) => r.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/**
 * One question: **who is in charge, and what is vacant?**
 *
 * A vacant critical role is the loudest thing on the screen, because
 * `dashboard-requirements.md` §2 makes it a readiness defect on a par with an
 * overdue drill — and because a list that simply omits what is missing is a
 * list that gets shorter as things get worse.
 */
export function Eco({
  orgId,
  sites,
  siteId,
  onSite,
  embedded = false,
}: {
  orgId: string;
  sites: api.Site[];
  siteId: string;
  onSite: (id: string) => void;
  /**
   * Composed inside another surface already scoped to one building — the
   * Locations building page's "Wardens" section (locations.tsx → the `Sites`
   * detail body). When set it suppresses two things that only make sense
   * standalone:
   *
   * - the `PageTitle` (an `<h1>`) — it would render *under* the building page's
   *   own `<h1>` title and the section's `<h2>`, an out-of-order heading (axe /
   *   WCAG heading-order); a plain `<h3>` sub-label takes its place;
   * - the `SitePicker` — a building switcher inside a page already scoped to one
   *   building is confusing, and the page is opened for a specific site.
   *
   * Additive and default-off, so the standalone/Overview caller is unchanged.
   */
  embedded?: boolean;
}) {
  const qc = useQueryClient();
  const [role, setRole] = useState(ECO_ROLES[0]!);
  const [userId, setUserId] = useState('');
  const [zoneId, setZoneId] = useState('');

  const board = useQuery({
    queryKey: ['eco', orgId, siteId],
    queryFn: () => api.getEco(orgId, siteId),
    enabled: Boolean(siteId),
  });

  const zones = useQuery({
    queryKey: ['zones', orgId, siteId],
    queryFn: () => api.getZones(orgId, siteId),
    enabled: Boolean(siteId),
  });

  /** The roles AS 3745 gives an *area*, as opposed to the whole building. */
  const areaScoped = role === 'area_warden' || role === 'floor_warden' || role === 'warden';

  const roster = useQuery({ queryKey: ['roster', orgId], queryFn: () => api.getRoster(orgId) });

  const assign = useMutation({
    mutationFn: () =>
      api.assignEco(orgId, siteId, {
        userId,
        role,
        ...(areaScoped && zoneId ? { zoneId } : {}),
      }),
    onSuccess: () => {
      setZoneId('');
      void qc.invalidateQueries({ queryKey: ['eco', orgId, siteId] });
      // The sweep-readiness gap ("N areas have no warden") reads this too.
      void qc.invalidateQueries({ queryKey: ['readiness', orgId, siteId] });
    },
  });

  const vacate = useMutation({
    mutationFn: (id: string) => api.vacateEco(orgId, siteId, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['eco', orgId, siteId] }),
  });

  const people = (roster.data?.members ?? []).filter((m) => !m.deprovisioned_at);

  return (
    <>
      {embedded ? (
        // A sub-label, not a page title: the Locations building page's "Wardens"
        // section owns the <h2> above this, so a second <h1> here would break
        // heading order (item 3).
        <h3 className="mb-1 text-[17px] leading-6 font-[650]">Control organisation</h3>
      ) : (
        <PageTitle sub="The AS 3745 control organisation for this building.">
          Control organisation
        </PageTitle>
      )}

      {embedded ? (
        <p className="mb-6 text-[13px] text-[var(--ink-2)]">
          The AS 3745 control organisation for this building — who sweeps where.
        </p>
      ) : (
        <div className="mb-6">
          <SitePicker sites={sites} siteId={siteId} onChange={onSite} />
        </div>
      )}

      {board.isLoading || !board.data ? (
        <Spinner />
      ) : (
        <>
          {board.data.readiness.vacantCriticalRoles.map((r) => (
            <Finding key={r}>
              <strong>{humanRole(r)} is vacant.</strong> Nobody currently holds command of this
              building.
            </Finding>
          ))}

          <Section title="Who holds what">
            {board.data.assignments.length === 0 ? (
              <Empty>Nobody is assigned yet. Start with a Chief Warden.</Empty>
            ) : (
              <Table head={['Role', 'Person', 'Area', 'Succession', 'Training', '']}>
                {board.data.assignments.map((a) => (
                  <Row key={a.id}>
                    <Cell>{humanRole(a.role)}</Cell>
                    <Cell wide>{a.display_name ?? '—'}</Cell>
                    <Cell>{a.zone_name ?? 'Whole site'}</Cell>
                    <Cell numeric>{a.succession_order === 0 ? 'Primary' : a.succession_order}</Cell>
                    <Cell>
                      {a.trained_at === null ? (
                        <Status tone="attention">Never recorded</Status>
                      ) : a.training_overdue ? (
                        <Status tone="attention">Overdue</Status>
                      ) : (
                        <Status tone="safe">Current</Status>
                      )}
                    </Cell>
                    <Cell>
                      <Button
                        variant="quiet"
                        label={`Vacate ${humanRole(a.role)}`}
                        onClick={() => vacate.mutate(a.id)}
                      >
                        Vacate
                      </Button>
                    </Cell>
                  </Row>
                ))}
              </Table>
            )}

            <div className="mt-5 flex flex-wrap items-end gap-3">
              <div className="w-[220px]">
                <Select
                  label="Role"
                  value={role}
                  onChange={setRole}
                  options={ECO_ROLES.map((r) => ({ value: r, label: humanRole(r) }))}
                />
              </div>
              {areaScoped && (zones.data?.zones.length ?? 0) > 0 ? (
                <div className="w-[220px]">
                  <Select
                    label="Area"
                    value={zoneId}
                    onChange={setZoneId}
                    options={[
                      { value: '', label: 'Whole site' },
                      ...(zones.data?.zones ?? []).map((z) => ({ value: z.id, label: z.name })),
                    ]}
                  />
                </div>
              ) : null}
              <div className="w-[260px]">
                <Select
                  label="Person"
                  value={userId}
                  onChange={setUserId}
                  options={[
                    { value: '', label: 'Choose…' },
                    ...people.map((m) => ({
                      value: m.user_id,
                      label: m.display_name ?? m.email ?? m.user_id.slice(0, 8),
                    })),
                  ]}
                />
              </div>
              <Button
                variant="primary"
                disabled={!userId || assign.isPending}
                onClick={() => assign.mutate()}
              >
                Assign
              </Button>
            </div>
          </Section>
        </>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- presence */

/**
 * One question: **who is in the building now?**
 *
 * This is the honest denominator. The roster says who could be here; this says
 * who is, and on a Tuesday those differ by hundreds
 * (`roster-and-presence.md`). An abandoned visitor — one whose host has already
 * left — is called out by name, because nobody in the building knows they are
 * still in it.
 */
export function Presence({
  orgId,
  sites,
  siteId,
  onSite,
  hideSitePicker,
}: {
  orgId: string;
  sites: api.Site[];
  siteId: string;
  onSite: (id: string) => void;
  /**
   * Suppress the built-in site dropdown. Set when Presence is embedded under a
   * screen that already owns the one switcher (Home's building view), so the
   * two do not fight — see the note at the call site (H1).
   */
  hideSitePicker?: boolean;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');

  const board = useQuery({
    queryKey: ['presence', orgId, siteId],
    queryFn: () => api.getPresence(orgId, siteId),
    enabled: Boolean(siteId),
    refetchInterval: 30_000,
  });

  const add = useMutation({
    mutationFn: () => api.signIn(orgId, siteId, { name, kind: 'visitor', expectedMinutes: 240 }),
    onSuccess: () => {
      setName('');
      void qc.invalidateQueries({ queryKey: ['presence', orgId, siteId] });
    },
  });

  const out = useMutation({
    mutationFn: (id: string) => api.signOutPresence(orgId, siteId, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['presence', orgId, siteId] }),
  });

  return (
    <>
      <PageTitle sub="Who is on site now, and until when.">Presence</PageTitle>

      {hideSitePicker ? null : (
        <div className="mb-6">
          <SitePicker sites={sites} siteId={siteId} onChange={onSite} />
        </div>
      )}

      {board.isLoading || !board.data ? (
        <Spinner />
      ) : (
        <>
          <Metrics>
            <Metric label="On site" value={board.data.counts.total} />
            <Metric label="Members" value={board.data.counts.members} />
            <Metric label="Visitors" value={board.data.counts.visitors} />
            <Metric
              label="Abandoned"
              value={board.data.counts.abandoned}
              tone={board.data.counts.abandoned > 0 ? 'attention' : 'neutral'}
            />
          </Metrics>

          <div className="mt-6">
            {board.data.basis === 'roster' ? (
              <Finding tone="neutral">
                Nobody is signed in, so an alert here would go to the roster rather than to the
                people actually present.
              </Finding>
            ) : null}
          </div>

          <Section title="On site">
            {board.data.present.length === 0 ? (
              <Empty>Nobody is signed in.</Empty>
            ) : (
              <Table head={['Person', 'Type', 'Since', 'Until', 'Host', '']}>
                {board.data.present.map((p) => (
                  <Row key={p.id}>
                    <Cell wide>
                      {p.display_name ?? '—'}
                      {p.abandoned ? (
                        <span className="ml-2">
                          <Status tone="attention">Host has left</Status>
                        </span>
                      ) : null}
                    </Cell>
                    <Cell>{p.kind}</Cell>
                    <Cell>{new Date(p.signed_in_at).toLocaleTimeString()}</Cell>
                    <Cell>{new Date(p.expected_departure_at).toLocaleTimeString()}</Cell>
                    <Cell>{p.host ?? '—'}</Cell>
                    <Cell>
                      <Button
                        variant="quiet"
                        label={`Sign out ${p.display_name ?? 'this person'}`}
                        onClick={() => out.mutate(p.id)}
                      >
                        Sign out
                      </Button>
                    </Cell>
                  </Row>
                ))}
              </Table>
            )}

            <div className="mt-5 flex items-end gap-3">
              <div className="w-[280px]">
                <Field
                  label="Sign in a visitor"
                  value={name}
                  onChange={setName}
                  placeholder="Name"
                  hint="Four hours by default. They stop being counted when it lapses."
                />
              </div>
              <Button disabled={!name || add.isPending} onClick={() => add.mutate()}>
                Sign in
              </Button>
            </div>
          </Section>
        </>
      )}
    </>
  );
}

export { Label };
